import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    TextField,
    LinearProgress,
    Chip,
    Paper,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import {
    Calculate as CalculateIcon,
    MenuBook as EnglishIcon,
    Timer as TimerIcon,
    EmojiEvents as TrophyIcon,
    Refresh as RefreshIcon,
    CheckCircle as SuccessIcon,
    Cancel as ErrorIcon,
    Pin as PinIcon,
    TableChart as TableChartIcon,
    FilterList as FilterListIcon,
    ViewModule as ViewModuleIcon,
    TrendingDown as TrendingDownIcon,
    AccountTree as AccountTreeIcon,
    Power as PowerIcon,
    Description as DescriptionIcon,
    Functions as FunctionsIcon,
    PieChart as PieChartIcon,
    SwapHoriz as SwapHorizIcon,
    AspectRatio as AspectRatioIcon,
    CompareArrows as CompareArrowsIcon,
    Balance as BalanceIcon,
    Share as ShareIcon,
    Tag as TagIcon,
    Equalizer as EqualizerIcon,
    Category as CategoryIcon,
    Straighten as StraightenIcon,
    Flip as FlipIcon,
    SquareFoot as SquareFootIcon,
    ViewInAr as ViewInArIcon,
    Timeline as TimelineIcon,
    BarChart as BarChartIcon,
    Casino as CasinoIcon,
    MenuBook as MenuBookIcon,
    Translate as TranslateIcon,
    Spellcheck as SpellcheckIcon,
    ShortText as ShortTextIcon,
    FormatQuote as FormatQuoteIcon,
    Edit as EditIcon,
    LowPriority as LowPriorityIcon,
    AutoAwesome as AutoAwesomeIcon,
    Lightbulb as LightbulbIcon,
    Psychology as PsychologyIcon,
    SortByAlpha as SortByAlphaIcon,
    Extension as ExtensionIcon,
    Code as CodeIcon,
    AutoStories as AutoStoriesIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import api from "../../api";
import {
    STUDENT_GAME_SUBJECTS,
    MATH_TOPICS,
    ENGLISH_TOPICS,
    VERBAL_REASONING_TOPICS,
    NON_VERBAL_REASONING_TOPICS,
    getStudentGameSubjectByKey,
} from "../../config/studentGamesConfig";
import AnalogClock from "../../components/AnalogClock";
import InteractiveAnalogClock from "../../components/InteractiveAnalogClock";
import {
    generateRandomClockQuestion,
    formatTime,
} from "../../utils/clockQuestions";
import { generatePercentageQuestion } from "../../utils/percentageQuestions";
import { generatePatternQuestion } from "../../utils/patternQuestions";
import { generatePieChartQuestion } from "../../utils/pieChartQuestions";
import { generateNumberPlaceValueQuestionByKey } from "../../utils/numberPlaceValueQuestions";
import { generateArithmeticOperationsQuestionByKey } from "../../utils/arithmeticOperationsQuestions";
import { generateFractionsDecimalsPercentagesQuestion } from "../../utils/fractionsDecimalsPercentagesQuestions";
import { generateRatioProportionQuestionByKey } from "../../utils/ratioProportionQuestions";
import { generateAlgebraBasicsQuestion } from "../../utils/algebraBasicsQuestions";
import { generateGeometryShapeQuestionByKey } from "../../utils/geometryShapeQuestions";
import { generateMeasurementQuestionByKey } from "../../utils/measurementQuestions";
import { generateStatisticsDataQuestionByKey } from "../../utils/statisticsDataQuestions";
import { generateProblemSolvingQuestionByKey } from "../../utils/problemSolvingReasoningQuestions";
import PieChart from "../../components/PieChart";
import { generateEnglishQuestionByKey } from "../../utils/englishQuestions";
import { generateVerbalReasoningQuestionByKey } from "../../utils/verbalReasoningQuestions";
import { generateNonVerbalReasoningQuestionByKey } from "../../utils/nonVerbalReasoningQuestions";
import { ShapeBlock, ShapeSequence, ShapeGrid } from "../../components/NonVerbalShapeView";
import DOMPurify from "dompurify";

/** Renders `currentProblem.question` from DB — plain text or HTML from admin Quill. */
function GameQuestionRich({ value, sx, variant, ...rest }) {
    const raw = String(value ?? "");
    const clean = DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
    if (!clean) return null;
    return (
        <Typography
            component="div"
            variant={variant}
            sx={{
                "& p": { margin: 0 },
                "& p + p": { mt: 1 },
                "& ul, & ol": { pl: 2, my: 0.5 },
                ...sx,
            }}
            dangerouslySetInnerHTML={{ __html: clean }}
            {...rest}
        />
    );
}

const VERBAL_REASONING_ICON_MAP = {
    "vr-word-relationships": CompareArrowsIcon,
    "vr-alphabet-logic": SortByAlphaIcon,
    "vr-word-puzzles": ExtensionIcon,
    "vr-codes-logic": CodeIcon,
    "vr-sequence-pattern": TimelineIcon,
    "vr-comprehension": AutoStoriesIcon,
};

const ENGLISH_ICON_MAP = {
    MenuBook: MenuBookIcon,
    Translate: TranslateIcon,
    Spellcheck: SpellcheckIcon,
    ShortText: ShortTextIcon,
    FormatQuote: FormatQuoteIcon,
    Edit: EditIcon,
    LowPriority: LowPriorityIcon,
    AutoAwesome: AutoAwesomeIcon,
};

const ENGLISH_GAME_CARDS = [
    { gameKey: "eng-reading", title: "Reading & Comprehension", description: "Passages, inferences and vocabulary in context.", gradient: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)", iconColor: "#1565c0", buttonBg: "#1565c0", iconKey: "MenuBook" },
    { gameKey: "eng-vocabulary", title: "Vocabulary Builder", description: "Synonyms, antonyms, word meanings and context.", gradient: "linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)", iconColor: "#c2185b", buttonBg: "#c2185b", iconKey: "Translate" },
    { gameKey: "eng-spelling", title: "Spelling Smart", description: "Homophones, tricky spellings, prefixes and suffixes.", gradient: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)", iconColor: "#ef6c00", buttonBg: "#ef6c00", iconKey: "Spellcheck" },
    { gameKey: "eng-grammar", title: "Grammar Quest", description: "Parts of speech, tenses and sentence structure.", gradient: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)", iconColor: "#2e7d32", buttonBg: "#2e7d32", iconKey: "ShortText" },
    { gameKey: "eng-punctuation", title: "Punctuation Pro", description: "Commas, apostrophes and quotation marks.", gradient: "linear-gradient(135deg, #ede7f6 0%, #d1c4e9 100%)", iconColor: "#5e35b1", buttonBg: "#5e35b1", iconKey: "FormatQuote" },
    { gameKey: "eng-creative-writing", title: "Creative Writing", description: "Best imaginative sentences and story openings.", gradient: "linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)", iconColor: "#00838f", buttonBg: "#00838f", iconKey: "Edit" },
    { gameKey: "eng-cloze", title: "Cloze & Sentence Completion", description: "Fill in blanks and complete sentences.", gradient: "linear-gradient(135deg, #fffde7 0%, #fff9c4 100%)", iconColor: "#f9a825", buttonBg: "#f9a825", iconKey: "LowPriority" },
    { gameKey: "eng-language-techniques", title: "Language Techniques", description: "Similes, metaphors, onomatopoeia and rhyme.", gradient: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)", iconColor: "#8e24aa", buttonBg: "#8e24aa", iconKey: "AutoAwesome" },
];

const VERBAL_REASONING_GAME_CARDS = [
    { gameKey: "vr-word-relationships", title: "Word Relationships", description: "Synonyms, antonyms and odd one out.", gradient: "linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)", iconColor: "#c2185b", buttonBg: "#c2185b" },
    { gameKey: "vr-alphabet-logic", title: "Alphabet & Letter Logic", description: "Letter sequences, missing letters and simple codes.", gradient: "linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)", iconColor: "#3949ab", buttonBg: "#3949ab" },
    { gameKey: "vr-word-puzzles", title: "Word Puzzles", description: "Hidden words and compound words.", gradient: "linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)", iconColor: "#0277bd", buttonBg: "#0277bd" },
    { gameKey: "vr-codes-logic", title: "Codes & Logic", description: "Letter-number codes and word codes.", gradient: "linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)", iconColor: "#ff8f00", buttonBg: "#ff8f00" },
    { gameKey: "vr-sequence-pattern", title: "Sequence & Pattern", description: "Word and letter patterns to complete.", gradient: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)", iconColor: "#2e7d32", buttonBg: "#2e7d32" },
    { gameKey: "vr-comprehension", title: "Comprehension & Inference", description: "Tiny stories with questions to infer meaning.", gradient: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)", iconColor: "#8e24aa", buttonBg: "#8e24aa" },
];

const NON_VERBAL_REASONING_GAME_CARDS = [
    { gameKey: "nvr-pattern-sequence", title: "Pattern & Sequence", description: "Find what comes next in number, letter or shape patterns.", gradient: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)", iconColor: "#e65100", buttonBg: "#e65100" },
    { gameKey: "nvr-shape-analogies", title: "Shape Analogies", description: "A is to B as C is to ?. Work out the rule.", gradient: "linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)", iconColor: "#3949ab", buttonBg: "#3949ab" },
    { gameKey: "nvr-odd-one-out", title: "Odd One Out", description: "Spot the number, letter or shape that does not belong.", gradient: "linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)", iconColor: "#c2185b", buttonBg: "#c2185b" },
    { gameKey: "nvr-rotation-reflection", title: "Rotation & Reflection", description: "Imagine how shapes and arrows look when turned.", gradient: "linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)", iconColor: "#0277bd", buttonBg: "#0277bd" },
    { gameKey: "nvr-matrix", title: "Matrix", description: "Find the missing number or shape in the grid.", gradient: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)", iconColor: "#2e7d32", buttonBg: "#2e7d32" },
    { gameKey: "nvr-spatial", title: "Spatial Reasoning", description: "Cubes, stacks and simple 3D arrangements.", gradient: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)", iconColor: "#7b1fa2", buttonBg: "#7b1fa2" },
    { gameKey: "nvr-shape-codes", title: "Shape Codes", description: "Match shape rules and letter-number codes.", gradient: "linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)", iconColor: "#ff8f00", buttonBg: "#ff8f00" },
    { gameKey: "nvr-hidden-shapes", title: "Hidden & Composite Shapes", description: "Find hidden shapes or numbers inside patterns.", gradient: "linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)", iconColor: "#00838f", buttonBg: "#00838f" },
];

const Games = () => {
    const navigate = useNavigate();
    const { subjectKey } = useParams();

    const [selectedGame, setSelectedGame] = useState(null);
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(60);
    const [gameActive, setGameActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentProblem, setCurrentProblem] = useState(null);
    const [userAnswer, setUserAnswer] = useState("");
    const [feedback, setFeedback] = useState(null);
    const [selectedSubject, setSelectedSubject] = useState(() =>
        subjectKey ? getStudentGameSubjectByKey(subjectKey) || null : null
    );
    const [selectedMathTopic, setSelectedMathTopic] = useState(null);
    const [selectedEnglishTopic, setSelectedEnglishTopic] = useState(null);
    const [selectedVerbalReasoningTopic, setSelectedVerbalReasoningTopic] = useState(null);
    const [selectedNonVerbalReasoningTopic, setSelectedNonVerbalReasoningTopic] = useState(null);

    /** Topics fetched from API (game_topics table); keyed by subject key. Falls back to static config if empty. */
    const [gameTopicsBySubject, setGameTopicsBySubject] = useState({});

    /** Question pool from DB for current game type; avoid repeating same question. */
    const [questionPool, setQuestionPool] = useState([]);
    const usedQuestionIdsRef = useRef(new Set());
    /** Track content we've already shown this session (by fingerprint) so we never repeat. */
    const shownContentFingerprintsRef = useRef(new Set());

    useEffect(() => {
        if (subjectKey) {
            const subject = getStudentGameSubjectByKey(subjectKey);
            setSelectedSubject(subject || null);
        } else {
            setSelectedSubject(null);
            setSelectedGame(null);
        }
        setSelectedMathTopic(null);
        setSelectedEnglishTopic(null);
        setSelectedVerbalReasoningTopic(null);
        setSelectedNonVerbalReasoningTopic(null);
    }, [subjectKey]);

    /** Fetch game topics from API when a subject is selected (saved in game_topics table). */
    useEffect(() => {
        if (!selectedSubject?.key) return;
        api.get(`student/games/topics?subject_key=${selectedSubject.key}`)
            .then((res) => {
                if (res.data?.success && res.data?.data?.topics?.length) {
                    setGameTopicsBySubject((prev) => ({
                        ...prev,
                        [selectedSubject.key]: res.data.data.topics,
                    }));
                }
            })
            .catch(() => {});
    }, [selectedSubject?.key]);

    /** Topics for the current subject: from API (game_topics) when available, else static config. */
    const getTopicsForSubject = (key) => {
        const fromApi = gameTopicsBySubject[key];
        if (fromApi?.length) return fromApi;
        const staticMap = {
            maths: MATH_TOPICS,
            english: ENGLISH_TOPICS,
            "verbal-reasoning": VERBAL_REASONING_TOPICS,
            "non-verbal-reasoning": NON_VERBAL_REASONING_TOPICS,
        };
        return staticMap[key] ?? [];
    };

    /** Get a single topic by subject key and topic key (for filtering game cards). */
    const getTopicByKey = (subjectKey, topicKey) => {
        if (!topicKey) return null;
        return getTopicsForSubject(subjectKey).find((t) => t.key === topicKey) ?? null;
    };

    // English Game specific states
    const [englishPool, setEnglishPool] = useState([]); // [{ char, id, used }]
    const [englishSelection, setEnglishSelection] = useState([]); // [{ char, id }]
    const [hintsLeft, setHintsLeft] = useState(3);

    const isMixedGame = selectedGame === "math-mixed";
    const isMathGame =
        selectedGame &&
        selectedGame.startsWith("math") &&
        (!isMixedGame || currentProblem?.type === "math");
    const isClockGame =
        selectedGame === "clock-mcq" ||
        (isMixedGame && currentProblem?.type === "clock");
    const isPercentGame =
        selectedGame === "percent-quest" ||
        (isMixedGame && currentProblem?.type === "percent");
    const isPatternGame =
        selectedGame === "pattern-quest" ||
        (isMixedGame && currentProblem?.type === "pattern");
    const isPieGame =
        selectedGame === "pie-chart" ||
        (isMixedGame && currentProblem?.type === "pie");
    const ENGLISH_MCQ_GAME_KEYS = [
        "eng-reading",
        "eng-vocabulary",
        "eng-spelling",
        "eng-grammar",
        "eng-punctuation",
        "eng-creative-writing",
        "eng-cloze",
        "eng-language-techniques",
    ];
    const VERBAL_REASONING_GAME_KEYS = [
        "vr-word-relationships",
        "vr-alphabet-logic",
        "vr-word-puzzles",
        "vr-codes-logic",
        "vr-sequence-pattern",
        "vr-comprehension",
    ];
    const NON_VERBAL_REASONING_GAME_KEYS = [
        "nvr-pattern-sequence",
        "nvr-shape-analogies",
        "nvr-odd-one-out",
        "nvr-rotation-reflection",
        "nvr-matrix",
        "nvr-spatial",
        "nvr-shape-codes",
        "nvr-hidden-shapes",
    ];
    const NUMBER_PLACE_VALUE_GAME_KEYS = [
        "number-read-compare",
        "place-value-rounding",
        "prime-factors-multiples",
        "square-cube",
        "negative-sequences",
    ];
    const ARITHMETIC_OPERATIONS_GAME_KEYS = [
        "long-mult-div",
        "bodmas",
        "powers-of-10",
        "word-problems",
    ];
    const FRACTIONS_DECIMALS_PERCENTAGES_GAME_KEYS = [
        "equivalent-fractions",
        "fraction-decimal-percent",
        "fraction-ops",
    ];
    const RATIO_PROPORTION_GAME_KEYS = [
        "ratio-notation",
        "ratio-proportion-problems",
        "sharing-in-ratio",
    ];
    const ALGEBRA_BASICS_GAME_KEYS = [
        "algebra-notation",
        "algebra-equations",
    ];
    const GEOMETRY_SHAPE_GAME_KEYS = [
        "geometry-2d-3d",
        "geometry-angles",
        "geometry-symmetry",
        "geometry-perimeter-area",
        "geometry-volume",
    ];
    const MEASUREMENT_GAME_KEYS = [
        "metric-conversion",
        "measurement-problems",
    ];
    const STATISTICS_DATA_GAME_KEYS = [
        "mean-median-mode",
        "probability-basics",
    ];
    const PROBLEM_SOLVING_REASONING_GAME_KEYS = [
        "multi-step-reasoning",
        "logic-reasoning",
    ];
    const isEnglishMcqGame = selectedGame && ENGLISH_MCQ_GAME_KEYS.includes(selectedGame);
    const isNumberPlaceValueGame = selectedGame && NUMBER_PLACE_VALUE_GAME_KEYS.includes(selectedGame);
    const isArithmeticOperationsGame = selectedGame && ARITHMETIC_OPERATIONS_GAME_KEYS.includes(selectedGame);
    const isFractionsDecimalsPercentagesGame = selectedGame && FRACTIONS_DECIMALS_PERCENTAGES_GAME_KEYS.includes(selectedGame);
    const isRatioProportionGame = selectedGame && RATIO_PROPORTION_GAME_KEYS.includes(selectedGame);
    const isAlgebraBasicsGame = selectedGame && ALGEBRA_BASICS_GAME_KEYS.includes(selectedGame);
    const isGeometryShapeGame = selectedGame && GEOMETRY_SHAPE_GAME_KEYS.includes(selectedGame);
    const isMeasurementGame = selectedGame && MEASUREMENT_GAME_KEYS.includes(selectedGame);
    const isStatisticsDataGame = selectedGame && STATISTICS_DATA_GAME_KEYS.includes(selectedGame);
    const isProblemSolvingReasoningGame = selectedGame && PROBLEM_SOLVING_REASONING_GAME_KEYS.includes(selectedGame);
    const isVerbalReasoningGame = selectedGame && VERBAL_REASONING_GAME_KEYS.includes(selectedGame);
    const isNonVerbalReasoningGame = selectedGame && NON_VERBAL_REASONING_GAME_KEYS.includes(selectedGame);

    const [difficulty, setDifficulty] = useState("normal"); // 'easy' | 'normal' | 'hard'
    const [difficultyPickerFor, setDifficultyPickerFor] = useState(null); // game type awaiting difficulty
    const [specificPickerGame, setSpecificPickerGame] = useState(null); // 'math-mul' | 'math-div' when choosing specific table/divisor
    const [specificPickerDifficulty, setSpecificPickerDifficulty] = useState(null);
    const [specificFactor, setSpecificFactor] = useState(null); // chosen multiplier/divisor, or null for mix
    const hasSubmittedScoreRef = useRef(false);

    const [clockSetHours, setClockSetHours] = useState(12);
    const [clockSetMinutes, setClockSetMinutes] = useState(0);

    const getClockLevelForDifficulty = () => {
        if (difficulty === "easy") return 1;
        if (difficulty === "hard") return 3;
        return 2;
    };

    const clockContentFilter = (c) => c && (c.mode === "set" || c.mode === "read" || c.mode === "match" || c.question_type?.startsWith?.("clock"));
    const generateClockProblem = () => {
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool(selectedGame === "math-mixed" ? clockContentFilter : null);
        if (fromPool?.content) {
            setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "clock" });
            if (fromPool.content.mode === "set") {
                setClockSetHours(12);
                setClockSetMinutes(0);
            }
            return;
        }
        let question = generateRandomClockQuestion(getClockLevelForDifficulty());
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool(selectedGame === "math-mixed" ? clockContentFilter : null);
            if (fromPool?.content) {
                setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "clock" });
                if (fromPool.content.mode === "set") {
                    setClockSetHours(12);
                    setClockSetMinutes(0);
                }
                return;
            }
            question = generateRandomClockQuestion(getClockLevelForDifficulty());
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem({ ...question, type: "clock" });
        saveGeneratedQuestion(question, selectedGame || "clock-mcq");
        if (question.mode === "set") {
            setClockSetHours(12);
            setClockSetMinutes(0);
        }
    };

    const percentContentFilter = (c) => c && (c.type === "percent" || c.question_type === "percent" || (c.question && c.options && c.answer && !c.mode && !c.hours));
    const generatePercentProblem = () => {
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool(selectedGame === "math-mixed" ? percentContentFilter : null);
        if (fromPool?.content) {
            setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "percent" });
            return;
        }
        let question = generatePercentageQuestion(difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool(selectedGame === "math-mixed" ? percentContentFilter : null);
            if (fromPool?.content) {
                setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "percent" });
                return;
            }
            question = generatePercentageQuestion(difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, selectedGame || "percent-quest");
    };

    const patternContentFilter = (c) => c && (c.type === "pattern" || (c.sequenceStr != null || c.sequence != null));
    const generatePatternProblem = () => {
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool(selectedGame === "math-mixed" ? patternContentFilter : null);
        if (fromPool?.content) {
            setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "pattern" });
            return;
        }
        let question = generatePatternQuestion(difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool(selectedGame === "math-mixed" ? patternContentFilter : null);
            if (fromPool?.content) {
                setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "pattern" });
                return;
            }
            question = generatePatternQuestion(difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, selectedGame || "pattern-quest");
    };

    const pieContentFilter = (c) => c && (c.type === "pie" || (c.values != null && Array.isArray(c.values)) || c.chartData);
    const generatePieProblem = () => {
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool(selectedGame === "math-mixed" ? pieContentFilter : null);
        if (fromPool?.content) {
            setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "pie" });
            return;
        }
        let question = generatePieChartQuestion(difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool(selectedGame === "math-mixed" ? pieContentFilter : null);
            if (fromPool?.content) {
                setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "pie" });
                return;
            }
            question = generatePieChartQuestion(difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, selectedGame || "pie-chart");
    };

    const generateNumberPlaceValueProblem = (gameType = null) => {
        const game = gameType || selectedGame;
        if (!game) return;
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool();
        if (fromPool?.content) {
            setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
            return;
        }
        let question = generateNumberPlaceValueQuestionByKey(game, difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool();
            if (fromPool?.content) {
                setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
                return;
            }
            question = generateNumberPlaceValueQuestionByKey(game, difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, game);
    };

    const generateArithmeticOperationsProblem = (gameType = null) => {
        const game = gameType || selectedGame;
        if (!game) return;
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool();
        if (fromPool?.content) {
            setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
            return;
        }
        let question = generateArithmeticOperationsQuestionByKey(game, difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool();
            if (fromPool?.content) {
                setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
                return;
            }
            question = generateArithmeticOperationsQuestionByKey(game, difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, game);
    };

    const generateFractionsDecimalsPercentagesProblem = (gameType = null) => {
        const game = gameType || selectedGame;
        if (!game) return;
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool();
        if (fromPool?.content) {
            setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
            return;
        }
        let question = generateFractionsDecimalsPercentagesQuestion(game, difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool();
            if (fromPool?.content) {
                setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
                return;
            }
            question = generateFractionsDecimalsPercentagesQuestion(game, difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, game);
    };

    const generateRatioProportionProblem = (gameType = null) => {
        const game = gameType || selectedGame;
        if (!game) return;
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool();
        if (fromPool?.content) {
            setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
            return;
        }
        let question = generateRatioProportionQuestionByKey(game, difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool();
            if (fromPool?.content) {
                setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
                return;
            }
            question = generateRatioProportionQuestionByKey(game, difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, game);
    };

    const generateAlgebraBasicsProblem = (gameType = null) => {
        const game = gameType || selectedGame;
        if (!game) return;
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool();
        if (fromPool?.content) {
            setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
            return;
        }
        let question = generateAlgebraBasicsQuestion(game, difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool();
            if (fromPool?.content) {
                setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
                return;
            }
            question = generateAlgebraBasicsQuestion(game, difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, game);
    };

    const generateGeometryShapeProblem = (gameType = null) => {
        const game = gameType || selectedGame;
        if (!game) return;
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool();
        if (fromPool?.content) {
            setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
            return;
        }
        let question = generateGeometryShapeQuestionByKey(game, difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool();
            if (fromPool?.content) {
                setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
                return;
            }
            question = generateGeometryShapeQuestionByKey(game, difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, game);
    };

    const generateMeasurementProblem = (gameType = null) => {
        const game = gameType || selectedGame;
        if (!game) return;
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool();
        if (fromPool?.content) {
            setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
            return;
        }
        let question = generateMeasurementQuestionByKey(game, difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool();
            if (fromPool?.content) {
                setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
                return;
            }
            question = generateMeasurementQuestionByKey(game, difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, game);
    };

    const generateStatisticsDataProblem = (gameType = null) => {
        const game = gameType || selectedGame;
        if (!game) return;
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool();
        if (fromPool?.content) {
            setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
            return;
        }
        let question = generateStatisticsDataQuestionByKey(game, difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool();
            if (fromPool?.content) {
                setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
                return;
            }
            question = generateStatisticsDataQuestionByKey(game, difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, game);
    };

    const generateProblemSolvingProblem = (gameType = null) => {
        const game = gameType || selectedGame;
        if (!game) return;
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool();
        if (fromPool?.content) {
            setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
            return;
        }
        let question = generateProblemSolvingQuestionByKey(game, difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool();
            if (fromPool?.content) {
                setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
                return;
            }
            question = generateProblemSolvingQuestionByKey(game, difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, game);
    };

    const generateEnglishMcqProblem = (gameType = null) => {
        const game = gameType || selectedGame;
        if (!game) return;
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool();
        if (fromPool?.content) {
            setCurrentProblem(normalizeContentFromDb(fromPool.content));
            return;
        }
        let question = generateEnglishQuestionByKey(game, difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool();
            if (fromPool?.content) {
                setCurrentProblem(normalizeContentFromDb(fromPool.content));
                return;
            }
            question = generateEnglishQuestionByKey(game, difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, game);
    };

    const generateVerbalReasoningProblem = (gameType = null) => {
        const game = gameType || selectedGame;
        if (!game) return;
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool();
        if (fromPool?.content) {
            setCurrentProblem(normalizeContentFromDb(fromPool.content));
            return;
        }
        let question = generateVerbalReasoningQuestionByKey(game, difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool();
            if (fromPool?.content) {
                setCurrentProblem(normalizeContentFromDb(fromPool.content));
                return;
            }
            question = generateVerbalReasoningQuestionByKey(game, difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, game);
    };

    const generateNonVerbalReasoningProblem = (gameType = null) => {
        const game = gameType || selectedGame;
        if (!game) return;
        const shown = shownContentFingerprintsRef.current;
        let fromPool = getNextFromPool();
        if (fromPool?.content) {
            setCurrentProblem(normalizeContentFromDb(fromPool.content));
            return;
        }
        let question = generateNonVerbalReasoningQuestionByKey(game, difficulty);
        for (let r = 0; r < 10 && shown.has(getContentFingerprint(question)); r++) {
            fromPool = getNextFromPool();
            if (fromPool?.content) {
                setCurrentProblem(normalizeContentFromDb(fromPool.content));
                return;
            }
            question = generateNonVerbalReasoningQuestionByKey(game, difficulty);
        }
        shown.add(getContentFingerprint(question));
        setCurrentProblem(question);
        saveGeneratedQuestion(question, game);
    };

    const generateMixedProblem = (levelOverride) => {
        const modes = ["math", "clock", "percent", "pattern", "pie"];
        const mode = modes[Math.floor(Math.random() * modes.length)];
        if (mode === "math") {
            generateMathProblem("math-mixed", levelOverride);
        } else if (mode === "clock") {
            generateClockProblem();
        } else if (mode === "percent") {
            generatePercentProblem();
        } else if (mode === "pattern") {
            generatePatternProblem();
        } else {
            generatePieProblem();
        }
    };

    // Persist score for a completed game attempt
    const submitGameScore = async (reason = "completed") => {
        if (!selectedGame || score <= 0 || hasSubmittedScoreRef.current) return;
        hasSubmittedScoreRef.current = true;
        try {
            await api.post("student/games/score", {
                game_type: selectedGame,
                difficulty,
                total_points: score,
                reason,
            });
        } catch (err) {
            console.error("Failed to submit game score", err);
        }
    };

    // Math Game Logic
    const generateMathProblem = async (mode, levelOverride) => {
        const gameMode = mode || selectedGame || "math-mixed";
        const isMathOnly = (c) => c && (c.type === "math" || (c.question != null && c.answer != null && !c.mode && !c.question_type));
        let fromPool = gameMode === "math-mixed"
            ? getNextFromPool(isMathOnly)
            : getNextFromPool();
        if (fromPool?.content) {
            setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            let op;
            let a;
            let b;

            const effectiveDifficulty = levelOverride || difficulty;

            // Decide operator set based on selected math mode
            switch (gameMode) {
                case "math-add":
                    op = "+";
                    break;
                case "math-sub":
                    op = "-";
                    break;
                case "math-mul":
                    op = "*";
                    break;
                case "math-div":
                    op = "/";
                    break;
                default:
                    // mixed mode
                    op = ["+", "-", "*", "/"][Math.floor(Math.random() * 4)];
            }

            const randInRange = (min, max) =>
                Math.floor(Math.random() * (max - min + 1)) + min;

            if (op === "/") {
                // Ensure clean integer division, divisor range depends on difficulty
                let divMin = 1;
                let divMax = 5;
                let qMin = 1;
                let qMax = 9;

                if (effectiveDifficulty === "normal") {
                    divMin = 5;
                    divMax = 10;
                    qMin = 2;
                    qMax = 12;
                } else if (effectiveDifficulty === "hard") {
                    divMin = 10;
                    divMax = 50;
                    qMin = 2;
                    qMax = 20;
                }

                // If user chose a specific divisor for division game, lock to that
                let divisor =
                    selectedGame === "math-div" && specificFactor
                        ? specificFactor
                        : randInRange(divMin, divMax);
                const quotient = randInRange(qMin, qMax);
                a = divisor * quotient; // dividend
                b = divisor;
            } else if (op === "*") {
                // Multiplication difficulty based on factors
                let minF = 1;
                let maxF = 5;

                if (effectiveDifficulty === "normal") {
                    minF = 5;
                    maxF = 10;
                } else if (effectiveDifficulty === "hard") {
                    minF = 10;
                    maxF = 50;
                }

                if (selectedGame === "math-mul" && specificFactor) {
                    // Lock one factor to the chosen specific value
                    a = randInRange(minF, maxF);
                    b = specificFactor;
                } else {
                    a = randInRange(minF, maxF);
                    b = randInRange(minF, maxF);
                }
            } else {
                // Addition / subtraction ranges by digit count
                let minVal = 1;
                let maxVal = 9; // one-digit default (easy)

                if (effectiveDifficulty === "normal") {
                    minVal = 10;
                    maxVal = 99; // two-digit
                } else if (effectiveDifficulty === "hard") {
                    minVal = 100;
                    maxVal = 999; // three-digit
                }

                if (op === "+") {
                    a = randInRange(minVal, maxVal);
                    b = randInRange(minVal, maxVal);
                } else {
                    // subtraction: ensure non-negative by choosing a >= b
                    const x = randInRange(minVal, maxVal);
                    const y = randInRange(minVal, maxVal);
                    a = Math.max(x, y);
                    b = Math.min(x, y);
                }
            }

            const expr = `${a} ${op} ${b}`;

            // For security and following user request, we could use api.mathjs.org
            const response = await axios.post('https://api.mathjs.org/v4/', {
                expr: [expr],
                precision: 4,
            });

            const rawResult = Array.isArray(response.data.result)
                ? response.data.result[0]
                : response.data.result;
            const num = Number(rawResult);
            let answerStr;
            if (!Number.isNaN(num) && Number.isFinite(num)) {
                const rounded = Number(num.toFixed(1));
                answerStr =
                    rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
            } else {
                answerStr = String(rawResult);
            }

            const problem = {
                question: expr,
                answer: answerStr,
                type: "math",
            };
            const shown = shownContentFingerprintsRef.current;
            if (shown.has(getContentFingerprint(problem))) {
                for (let r = 0; r < 5; r++) {
                    fromPool = gameMode === "math-mixed" ? getNextFromPool(isMathOnly) : getNextFromPool();
                    if (fromPool?.content) {
                        setCurrentProblem({ ...normalizeContentFromDb(fromPool.content), type: "math" });
                        setLoading(false);
                        return;
                    }
                }
            }
            shown.add(getContentFingerprint(problem));
            setCurrentProblem(problem);
            saveGeneratedQuestion(problem, gameMode);
        } catch (error) {
            console.error("MathJS API Error:", error);
            // Fallback
            const a = Math.floor(Math.random() * 10);
            const b = Math.floor(Math.random() * 10);
            const fallback = {
                question: `${a} + ${b}`,
                answer: (a + b).toString(),
                type: "math",
            };
            shownContentFingerprintsRef.current.add(getContentFingerprint(fallback));
            setCurrentProblem(fallback);
            saveGeneratedQuestion(fallback, gameMode);
        }
        setLoading(false);
    };

    // English Game Logic (Datamuse)
    const generateEnglishProblem = async () => {
        setLoading(true);
        try {
            const topics = ['science', 'nature', 'technology', 'education', 'space', 'ocean', 'animals', 'history'];
            const topic = topics[Math.floor(Math.random() * topics.length)];
            const response = await axios.get(`https://api.datamuse.com/words?ml=${topic}&max=50`);

            const words = response.data.filter(w => w.word.length > 3 && w.word.length < 10 && !w.word.includes(' ') && !w.word.includes('-'));
            const targetWord = words[Math.floor(Math.random() * words.length)].word.toUpperCase();

            // Create pool of characters with unique IDs
            const pool = targetWord.split('').map((char, index) => ({
                char,
                id: `char-${index}-${Math.random().toString(36).substr(2, 9)}`,
                used: false
            })).sort(() => Math.random() - 0.5);

            setCurrentProblem({
                answer: targetWord,
                type: 'english',
                hint: `Topic: ${topic}`
            });
            setEnglishPool(pool);
            setEnglishSelection([]);
            setUserAnswer("");
        } catch (error) {
            console.error("Datamuse API Error:", error);
            const word = "ORANGE";
            const pool = word.split('').map((char, index) => ({
                char,
                id: `char-${index}`,
                used: false
            })).sort(() => Math.random() - 0.5);
            setCurrentProblem({
                answer: word,
                type: 'english',
                hint: "Color or fruit"
            });
            setEnglishPool(pool);
            setEnglishSelection([]);
            setUserAnswer("");
        }
        setLoading(false);
    };

    /** Stable fingerprint for content so we can detect duplicates in session. */
    const getContentFingerprint = (content) => {
        if (!content) return "";
        const q = content.question ?? content.instruction ?? "";
        const a = content.answer != null ? String(content.answer) : "";
        const o = Array.isArray(content.options) ? content.options.map((x) => (x && typeof x === "object" && "text" in x ? x.text : x)).join("|") : "";
        return `${q}\n${a}\n${o}`;
    };

    /** Normalize content from DB: options as string[] (for display/answer match), answer as string. */
    const normalizeContentFromDb = (content) => {
        if (!content) return content;
        const out = { ...content };
        if (Array.isArray(content.options)) {
            out.options = content.options.map((o) => (o && typeof o === "object" && "text" in o ? o.text : o));
        }
        if (out.answer != null) out.answer = String(out.answer);
        return out;
    };

    /** Fetch question pool from API so we can avoid repeating questions. */
    const fetchQuestionPool = (gameType) => {
        if (!gameType) return;
        api.get("student/games/questions", { params: { game_type: gameType, limit: 50 } })
            .then((res) => {
                if (res.data?.success && Array.isArray(res.data?.data?.questions)) {
                    setQuestionPool(res.data.data.questions);
                } else {
                    setQuestionPool([]);
                }
            })
            .catch(() => setQuestionPool([]));
    };

    /** Get one question from pool that hasn't been used this session and wasn't already shown (by content fingerprint). Optional filter(content) to pick only matching. */
    const getNextFromPool = (contentFilter = null) => {
        const pool = questionPool;
        const used = usedQuestionIdsRef.current;
        const shown = shownContentFingerprintsRef.current;
        let available = pool.filter((q) => q.id != null && !used.has(q.id));
        if (contentFilter && typeof contentFilter === "function") {
            available = available.filter((q) => contentFilter(q.content));
        }
        available = available.filter((q) => {
            const fp = getContentFingerprint(q.content);
            return !shown.has(fp);
        });
        if (available.length === 0) return null;
        const pick = available[Math.floor(Math.random() * available.length)];
        used.add(pick.id);
        shown.add(getContentFingerprint(pick.content));
        return pick;
    };

    /** Infer currentProblem.type from game type and content for display. */
    const inferProblemType = (gameType, content) => {
        if (gameType === "clock-mcq") return "clock";
        if (gameType === "percent-quest") return "percent";
        if (gameType === "pattern-quest") return "pattern";
        if (gameType === "pie-chart") return "pie";
        if (gameType === "math-mixed" && content) {
            if (content.mode === "set" || content.mode === "read" || content.mode === "match" || content.question_type?.startsWith("clock")) return "clock";
            if (content.type === "percent" || content.question_type === "percent") return "percent";
            if (content.type === "pattern") return "pattern";
            if (content.type === "pie") return "pie";
            return "math";
        }
        if (gameType && gameType.startsWith("math")) return "math";
        return "math";
    };

    /** Save a generated question to DB (deduplicated on backend). Call after generating locally. */
    const saveGeneratedQuestion = (problem, gameType) => {
        if (!gameType || !problem) return;
        const content = { ...problem };
        delete content.type;
        api.post("student/games/questions", {
            game_type: gameType,
            content,
            difficulty,
        }).catch(() => {});
    };

    const startGame = (type, level = difficulty) => {
        const effectiveDifficulty = level || difficulty || "normal";
        setDifficulty(effectiveDifficulty);
        setSelectedGame(type);
        setScore(0);
        setTimeLeft(60);
        setGameActive(true);
        setFeedback(null);
        setUserAnswer("");
        setHintsLeft(3); // Reset hints on new game
        hasSubmittedScoreRef.current = false;
        usedQuestionIdsRef.current = new Set();
        shownContentFingerprintsRef.current = new Set();
        fetchQuestionPool(type);
        if (type === "math-mixed") generateMixedProblem(effectiveDifficulty);
        else if (type.startsWith("math")) generateMathProblem(type, effectiveDifficulty);
        else if (type === "clock-mcq") generateClockProblem();
        else if (type === "percent-quest") generatePercentProblem();
        else if (type === "pattern-quest") generatePatternProblem();
        else if (type === "pie-chart") generatePieProblem();
        else if (NUMBER_PLACE_VALUE_GAME_KEYS.includes(type)) generateNumberPlaceValueProblem(type);
        else if (ARITHMETIC_OPERATIONS_GAME_KEYS.includes(type)) generateArithmeticOperationsProblem(type);
        else if (FRACTIONS_DECIMALS_PERCENTAGES_GAME_KEYS.includes(type)) generateFractionsDecimalsPercentagesProblem(type);
        else if (RATIO_PROPORTION_GAME_KEYS.includes(type)) generateRatioProportionProblem(type);
        else if (ALGEBRA_BASICS_GAME_KEYS.includes(type)) generateAlgebraBasicsProblem(type);
        else if (GEOMETRY_SHAPE_GAME_KEYS.includes(type)) generateGeometryShapeProblem(type);
        else if (MEASUREMENT_GAME_KEYS.includes(type)) generateMeasurementProblem(type);
        else if (STATISTICS_DATA_GAME_KEYS.includes(type)) generateStatisticsDataProblem(type);
        else if (PROBLEM_SOLVING_REASONING_GAME_KEYS.includes(type)) generateProblemSolvingProblem(type);
        else if (ENGLISH_MCQ_GAME_KEYS.includes(type)) generateEnglishMcqProblem(type);
        else if (VERBAL_REASONING_GAME_KEYS.includes(type)) generateVerbalReasoningProblem(type);
        else if (NON_VERBAL_REASONING_GAME_KEYS.includes(type)) generateNonVerbalReasoningProblem(type);
        else if (type === "english") generateEnglishProblem();
        else generateEnglishProblem();
    };

    useEffect(() => {
        let timer;
        if (gameActive && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
        } else if (timeLeft === 0 && gameActive) {
            setGameActive(false);
            submitGameScore("time_up");
        }
        return () => clearInterval(timer);
    }, [gameActive, timeLeft]);

    const handleAnswerSubmitRef = useRef(() => {});

    const handleAnswerSubmit = (manualAnswer = null) => {
        const finalAnswer = manualAnswer || userAnswer;
        if (!finalAnswer) return;

        const isCorrect = finalAnswer.trim() === currentProblem.answer;
        if (isCorrect) {
            // Award points based on difficulty
            const pointsPerCorrect =
                difficulty === "easy"
                    ? (Math.random() < 0.5 ? 2 : 3)
                    : difficulty === "hard"
                    ? 5
                    : 4;
            setScore(prev => prev + pointsPerCorrect);
            setFeedback({ type: 'success', message: 'Correct!' });
            setTimeout(() => {
                setFeedback(null);
                setUserAnswer("");
                if (selectedGame === "math-mixed") generateMixedProblem();
                else if (isMathGame) generateMathProblem();
                else if (isClockGame) generateClockProblem();
                else if (isPercentGame) generatePercentProblem();
                else if (isPatternGame) generatePatternProblem();
                else if (isPieGame) generatePieProblem();
                else if (isNumberPlaceValueGame) generateNumberPlaceValueProblem();
                else if (isArithmeticOperationsGame) generateArithmeticOperationsProblem();
                else if (isFractionsDecimalsPercentagesGame) generateFractionsDecimalsPercentagesProblem();
                else if (isRatioProportionGame) generateRatioProportionProblem();
                else if (isAlgebraBasicsGame) generateAlgebraBasicsProblem();
                else if (isGeometryShapeGame) generateGeometryShapeProblem();
                else if (isMeasurementGame) generateMeasurementProblem();
                else if (isStatisticsDataGame) generateStatisticsDataProblem();
                else if (isProblemSolvingReasoningGame) generateProblemSolvingProblem();
                else if (isEnglishMcqGame) generateEnglishMcqProblem();
                else if (isVerbalReasoningGame) generateVerbalReasoningProblem();
                else if (isNonVerbalReasoningGame) generateNonVerbalReasoningProblem();
                else generateEnglishProblem();
            }, 800);
        } else {
            setFeedback({ type: 'error', message: `Oops! Try again.` });
            setTimeout(() => {
                setFeedback(null);
                // Clear the user's answer but keep the same question to avoid confusion
                setUserAnswer("");

                if (selectedGame === 'english') {
                    // Reset letter tiles so the student can try the same word again
                    setEnglishSelection([]);
                    setEnglishPool(prev => prev.map(item => ({ ...item, used: false })));
                }
            }, 1200);
        }
    };

    handleAnswerSubmitRef.current = handleAnswerSubmit;

    // Handle clicks for English Game
    const handleAddLetter = (charObj) => {
        if (charObj.used || !!feedback) return;

        const newSelection = [...englishSelection, charObj];
        setEnglishSelection(newSelection);

        // Mark as used in pool
        setEnglishPool(prev => prev.map(item => item.id === charObj.id ? { ...item, used: true } : item));

        const newAnswer = newSelection.map(s => s.char).join("");
        setUserAnswer(newAnswer);

        // Auto-submit if all letters are used
        if (newAnswer.length === currentProblem.answer.length) {
            handleAnswerSubmit(newAnswer);
        }
    };

    const handleRemoveLetter = (charObj, index) => {
        if (!!feedback) return;

        // Only allow removing if it's the last selected or rearrange selection
        const newSelection = englishSelection.filter((_, i) => i !== index);
        setEnglishSelection(newSelection);

        // Mark as unused in pool
        setEnglishPool(prev => prev.map(item => item.id === charObj.id ? { ...item, used: false } : item));

        setUserAnswer(newSelection.map(s => s.char).join(""));
    };

    const handleClear = () => {
        if (!!feedback) return;

        if (selectedGame === 'english' && englishSelection.length > 0) {
            // Remove last selected letter
            const lastSelected = englishSelection[englishSelection.length - 1];
            const newSelection = englishSelection.slice(0, -1);
            setEnglishSelection(newSelection);

            // Mark as unused in pool
            setEnglishPool(prev => prev.map(item => item.id === lastSelected.id ? { ...item, used: false } : item));

            setUserAnswer(newSelection.map(s => s.char).join(""));
        } else if (isMathGame) {
            setUserAnswer("");
        }
    };

    const handleSkip = () => {
        if (!!feedback) return;
        generateEnglishProblem();
    };

    const handleHint = () => {
        if (hintsLeft <= 0 || !!feedback || !currentProblem) return;

        const nextCharIndex = englishSelection.length;
        if (nextCharIndex >= currentProblem.answer.length) return;

        const neededChar = currentProblem.answer[nextCharIndex];

        // Find this character in pool that isn't used
        const poolIndex = englishPool.findIndex(p => p.char === neededChar && !p.used);

        if (poolIndex !== -1) {
            const charObj = englishPool[poolIndex];
            handleAddLetter(charObj);
            setHintsLeft(prev => prev - 1);
        }
    };

    const handleKeyPress = (key) => {
        if (key === 'BACKSPACE') {
            setUserAnswer(prev => prev.slice(0, -1));
        } else if (key === 'ENTER') {
            handleAnswerSubmit();
        } else {
            setUserAnswer(prev => prev + key);
        }
    };

    // Physical keyboard support for math/numeric answer games (0-9, Backspace, Enter)
    useEffect(() => {
        if (!isMathGame || !currentProblem || feedback) return;

        const onKeyDown = (e) => {
            if (e.key === 'Backspace') {
                e.preventDefault();
                handleKeyPress('BACKSPACE');
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                handleAnswerSubmitRef.current();
                return;
            }
            if (e.key >= '0' && e.key <= '9') {
                e.preventDefault();
                handleKeyPress(e.key);
            }
        };

        window.addEventListener('keydown', onKeyDown, true); // capture so Enter submits even when keypad button is focused
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [isMathGame, currentProblem, feedback]);

    const MathKeyboard = () => {
        const mathKeys = [
            ['1', '2', '3'],
            ['4', '5', '6'],
            ['7', '8', '9'],
            ['BACKSPACE', '0', 'ENTER']
        ];

        return (
            <Box
                sx={{
                    mt: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.2,
                    px: { xs: 1, md: 4 },
                    pb: 2,
                }}
            >
                {mathKeys.map((row, rowIndex) => (
                    <Box
                        key={rowIndex}
                        sx={{
                            display: 'flex',
                            gap: 1.2,
                            justifyContent: 'center',
                            width: '100%',
                        }}
                    >
                        {row.map((key) => {
                            const isBackspace = key === 'BACKSPACE';
                            const isEnter = key === 'ENTER';

                            let bgColor = '#667eea'; // portal indigo
                            let shadowColor = '#4c51bf';
                            let hoverColor = '#5a67d8';

                            if (isBackspace) {
                                bgColor = '#ff7675'; // soft red from portal palette
                                shadowColor = '#d63031';
                                hoverColor = '#ff6b6b';
                            } else if (isEnter) {
                                bgColor = '#00b894'; // teal/green accent
                                shadowColor = '#00856a';
                                hoverColor = '#00a084';
                            }

                            return (
                                <Button
                                    key={key}
                                    variant="contained"
                                    onClick={() => handleKeyPress(key)}
                                    disabled={!!feedback}
                                    sx={{
                                        minWidth: { xs: 80, md: 95 },
                                        height: 64,
                                        borderRadius: 2.5,
                                        fontSize: '1.6rem',
                                        fontWeight: 700,
                                        background: bgColor,
                                        boxShadow: `0 4px 0 ${shadowColor}`,
                                        '&:hover': {
                                            background: hoverColor,
                                            transform: 'translateY(1px)',
                                            boxShadow: `0 3px 0 ${shadowColor}`,
                                        },
                                        '&:active': {
                                            transform: 'translateY(2px)',
                                            boxShadow: `0 2px 0 ${shadowColor}`,
                                        },
                                        transition: 'all 0.08s ease-out',
                                    }}
                                >
                                    {isBackspace
                                        ? '⌫'
                                        : isEnter
                                            ? <SuccessIcon sx={{ fontSize: '1.6rem' }} />
                                            : key}
                                </Button>
                            );
                        })}
                    </Box>
                ))}
            </Box>
        );
    };

    const EnglishGameUI = () => {
        if (!currentProblem) return null;

        return (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                {/* Unscramble section */}
                <Box sx={{ textAlign: 'center', width: '100%' }}>
                    <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>Unscramble the word:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                        {englishPool.map((charObj) => (
                            <Paper
                                key={charObj.id}
                                onClick={() => handleAddLetter(charObj)}
                                sx={{
                                    width: { xs: 50, md: 70 },
                                    height: { xs: 50, md: 70 },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: { xs: '1.5rem', md: '2rem' },
                                    fontWeight: 'bold',
                                    borderRadius: 3,
                                    cursor: charObj.used ? 'default' : 'pointer',
                                    bgcolor: charObj.used ? '#f0f0f0' : '#10b981',
                                    color: charObj.used ? 'transparent' : '#fff',
                                    visibility: charObj.used ? 'hidden' : 'visible',
                                    boxShadow: charObj.used ? 'none' : '0 6px 0 #059669',
                                    '&:hover': {
                                        transform: charObj.used ? 'none' : 'translateY(2px)',
                                        boxShadow: charObj.used ? 'none' : '0 4px 0 #059669',
                                    },
                                    transition: 'all 0.1s',
                                    opacity: charObj.used ? 0 : 1
                                }}
                            >
                                {charObj.char}
                            </Paper>
                        ))}
                    </Box>
                </Box>

                {/* Answer section */}
                <Box sx={{ textAlign: 'center', width: '100%' }}>
                    <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>Your answer:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', minHeight: 70 }}>
                        {/* Empty slots placeholders */}
                        {Array.from({ length: currentProblem.answer.length }).map((_, idx) => {
                            const selectedChar = englishSelection[idx];
                            return (
                                <Paper
                                    key={idx}
                                    onClick={() => selectedChar && handleRemoveLetter(selectedChar, idx)}
                                    sx={{
                                        width: { xs: 50, md: 70 },
                                        height: { xs: 50, md: 70 },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: { xs: '1.5rem', md: '2rem' },
                                        fontWeight: 'bold',
                                        borderRadius: 3,
                                        bgcolor: selectedChar ? '#fff' : '#f5f5f5',
                                        color: selectedChar ? '#10b981' : 'transparent',
                                        border: selectedChar ? '2px solid #10b981' : '2px dashed #ccc',
                                        cursor: selectedChar ? 'pointer' : 'default',
                                        '&:hover': {
                                            bgcolor: selectedChar ? '#ecfdf5' : '#f5f5f5'
                                        }
                                    }}
                                >
                                    {selectedChar?.char}
                                </Paper>
                            );
                        })}
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                        Tap a letter to remove it
                    </Typography>
                </Box>

                {/* Controls */}
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                        variant="contained"
                        color="inherit"
                        startIcon={<RefreshIcon />}
                        onClick={handleClear}
                        sx={{ borderRadius: 3, textTransform: 'none', px: 4, bgcolor: '#e5e7eb' }}
                    >
                        Clear
                    </Button>
                    <Button
                        variant="contained"
                        disabled={hintsLeft <= 0 || !!feedback}
                        onClick={handleHint}
                        sx={{
                            borderRadius: 3,
                            textTransform: 'none',
                            px: 4,
                            bgcolor: '#fcd34d',
                            color: '#92400e',
                            '&:hover': { bgcolor: '#fbbf24' },
                            '&.Mui-disabled': { bgcolor: '#fef3c7', color: '#d97706', opacity: 0.6 }
                        }}
                        startIcon={<TrophyIcon />}
                    >
                        Hint ({hintsLeft})
                    </Button>
                    <Button
                        variant="contained"
                        sx={{
                            borderRadius: 3,
                            textTransform: 'none',
                            px: 4,
                            bgcolor: '#bfdbfe',
                            color: '#1e40af',
                            '&:hover': { bgcolor: '#93c5fd' }
                        }}
                        onClick={handleSkip}
                        endIcon={<RefreshIcon sx={{ transform: 'rotate(90deg)' }} />}
                    >
                        Skip
                    </Button>
                </Box>
            </Box>
        );
    };

    const handleSubjectSelect = (subject) => {
        navigate(`/student/games/${subject.key}`);
    };

    const handleChangeSubject = () => {
        navigate(`/student/games`);
    };

    const renderSubjectSelection = () => (
        <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 2, color: '#111827' }}>
                🎮 Interactive Learning Games
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: "#374151" }}>
                Choose a subject to explore fun, interactive games.
            </Typography>
            <Grid container spacing={3}>
                {STUDENT_GAME_SUBJECTS.map((subject) => (
                    <Grid item xs={12} sm={6} md={3} key={subject.key}>
                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 4,
                                cursor: 'pointer',
                                background: subject.gradient,
                                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-6px)',
                                    boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                                },
                            }}
                            onClick={() => handleSubjectSelect(subject)}
                        >
                            <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    gutterBottom
                                    sx={{ color: '#111827' }}
                                >
                                    {subject.label}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: '#374151', mb: 2, minHeight: 48 }}
                                >
                                    {subject.description}
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="small"
                                    sx={{
                                        borderRadius: 999,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3,
                                        background: '#111827',
                                        '&:hover': { background: '#030712' },
                                    }}
                                >
                                    View Games
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    const subjectGames = selectedSubject ? selectedSubject.games : [];

    if (!selectedGame) {
        if (!selectedSubject) {
            return renderSubjectSelection();
        }

        return (
            <Box sx={{ position: 'relative' }}>
                {difficultyPickerFor && (
                    <Box
                        sx={{
                            position: "fixed",
                            inset: 0,
                            bgcolor: "rgba(15,23,42,0.45)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1200,
                        }}
                    >
                        <Paper
                            sx={{
                                p: 4,
                                borderRadius: 4,
                                maxWidth: 420,
                                width: "100%",
                                textAlign: "center",
                                boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
                            }}
                        >
                            <Typography variant="h5" fontWeight="bold" sx={{ mb: 1.5 }}>
                                Choose Difficulty
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 3, color: "#374151" }}>
                                How hard do you want it?
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                                {[
                                    {
                                        id: "easy",
                                        title: "Easy",
                                        icon: "😊",
                                        description: "Perfect for beginners! Smaller numbers and simpler patterns.",
                                        points: "2–3 points per correct",
                                        bg: "#e0f7fa",
                                        border: "#4ade80",
                                    },
                                    {
                                        id: "normal",
                                        title: "Normal",
                                        icon: "🎯",
                                        description: "Ready for a challenge? Standard questions for your grade.",
                                        points: "4 points per correct",
                                        bg: "#e0ebff",
                                        border: "#60a5fa",
                                    },
                                    {
                                        id: "hard",
                                        title: "Hard",
                                        icon: "🔥",
                                        description: "For true champions! Bigger numbers and trickier patterns.",
                                        points: "5 points per correct",
                                        bg: "#ffe4e6",
                                        border: "#fb7185",
                                    },
                                ].map((levelConfig) => (
                                    <Button
                                        key={levelConfig.id}
                                        onClick={() => {
                                            const level = levelConfig.id;
                                            const gameType = difficultyPickerFor;
                                            setDifficulty(level);
                                            setSpecificPickerDifficulty(level);
                                            setDifficultyPickerFor(null);
                                            // For pure multiplication/division games, open specific selector
                                            if (gameType === "math-mul" || gameType === "math-div") {
                                                setSpecificPickerGame(gameType);
                                                setSpecificFactor(null);
                                            } else if (gameType) {
                                                setSpecificFactor(null);
                                                startGame(gameType, level);
                                            }
                                        }}
                                        sx={{
                                            justifyContent: "flex-start",
                                            textAlign: "left",
                                            borderRadius: 3,
                                            px: 2.5,
                                            py: 1.5,
                                            border: `2px solid ${
                                                difficulty === levelConfig.id ? levelConfig.border : "#e5e7eb"
                                            }`,
                                            bgcolor:
                                                difficulty === levelConfig.id ? levelConfig.bg : "#ffffff",
                                            boxShadow:
                                                difficulty === levelConfig.id
                                                    ? "0 8px 20px rgba(15,23,42,0.15)"
                                                    : "none",
                                            textTransform: "none",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 2 }}>
                                            <Box
                                                sx={{
                                                    fontSize: "1.8rem",
                                                    width: 40,
                                                    textAlign: "center",
                                                }}
                                            >
                                                {levelConfig.icon}
                                            </Box>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    {levelConfig.title}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ display: "block", fontWeight: 600, color: "#374151" }}
                                                >
                                                    {levelConfig.points}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: "#374151" }}>
                                                    {levelConfig.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Button>
                                ))}
                            </Box>
                            <Button
                                variant="text"
                                onClick={() => {
                                    setDifficultyPickerFor(null);
                                    setSpecificPickerGame(null);
                                    setSpecificPickerDifficulty(null);
                                }}
                                sx={{ mt: 1 }}
                            >
                                Cancel
                            </Button>
                        </Paper>
                    </Box>
                )}
                {specificPickerGame && (
                    <Box
                        sx={{
                            position: "fixed",
                            inset: 0,
                            bgcolor: "rgba(15,23,42,0.45)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1300,
                        }}
                    >
                        <Paper
                            sx={{
                                p: 4,
                                borderRadius: 4,
                                maxWidth: 480,
                                width: "100%",
                                maxHeight: "80vh",
                                textAlign: "center",
                                boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Typography variant="h5" fontWeight="bold" sx={{ mb: 1.5 }}>
                                Choose Specific Number
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 3, color: "#374151" }}>
                                Pick the number to {specificPickerGame === "math-mul" ? "multiply by" : "divide by"}, or play Mixed.
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1.5,
                                    mb: 2,
                                    overflowY: "auto",
                                    pr: 1,
                                    flexGrow: 1,
                                }}
                            >
                                {(() => {
                                    const level = specificPickerDifficulty || difficulty || "normal";
                                    let values = [];
                                    if (level === "easy") {
                                        values = [1, 2, 3, 4, 5];
                                    } else if (level === "normal") {
                                        values = [5, 6, 7, 8, 9, 10];
                                    } else {
                                        values = [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
                                    }
                                    const labelPrefix =
                                        specificPickerGame === "math-mul" ? "× by " : "÷ by ";

                                    return [
                                        ...values.map((v) => (
                                            <Button
                                                key={v}
                                                onClick={() => {
                                                    setSpecificFactor(v);
                                                    const game = specificPickerGame;
                                                    const levelToUse = level;
                                                    setSpecificPickerGame(null);
                                                    startGame(game, levelToUse);
                                                }}
                                                sx={{
                                                    justifyContent: "flex-start",
                                                    textAlign: "left",
                                                    borderRadius: 3,
                                                    px: 2,
                                                    py: 0.9,
                                                    border: "2px solid #e5e7eb",
                                                    bgcolor: "#ffffff",
                                                    textTransform: "none",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                    <Box
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: "50%",
                                                            bgcolor: "#eef2ff",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontWeight: 800,
                                                            color: "#374151",
                                                        }}
                                                    >
                                                        {v}
                                                    </Box>
                                                    <Box sx={{ textAlign: "left" }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                            {labelPrefix}
                                                            {v}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: "#374151" }}>
                                                            All questions will use this number.
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Button>
                                        )),
                                        <Button
                                            key="mix"
                                            onClick={() => {
                                                setSpecificFactor(null);
                                                const game = specificPickerGame;
                                                const levelToUse = level;
                                                setSpecificPickerGame(null);
                                                startGame(game, levelToUse);
                                            }}
                                            sx={{
                                                justifyContent: "flex-start",
                                                textAlign: "left",
                                                borderRadius: 3,
                                                px: 2,
                                                py: 0.9,
                                                border: "2px solid #22c55e",
                                                bgcolor: "#dcfce7",
                                                textTransform: "none",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                <Box
                                                    sx={{
                                                            width: 32,
                                                            height: 32,
                                                        borderRadius: "50%",
                                                        bgcolor: "#bbf7d0",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontWeight: 800,
                                                        color: "#15803d",
                                                    }}
                                                >
                                                    🎲
                                                </Box>
                                                <Box sx={{ textAlign: "left" }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                        Mixed Numbers
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: "#166534" }}>
                                                        Use different numbers each time (current behaviour).
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Button>,
                                    ];
                                })()}
                            </Box>
                            <Button
                                variant="text"
                                onClick={() => {
                                    setSpecificPickerGame(null);
                                    setSpecificPickerDifficulty(null);
                                }}
                            >
                                Cancel
                            </Button>
                        </Paper>
                    </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box>
                        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: '#111827' }}>
                            🎮 Interactive Learning Games
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
                            {selectedSubject.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#374151' }}>
                            {selectedSubject.description}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        onClick={handleChangeSubject}
                        sx={{
                            borderRadius: 999,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                        }}
                    >
                        Change Subject
                    </Button>
                </Box>

                {/* Topic filters per subject */}
                {selectedSubject.key === "maths" && (
                    <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", gap: 1 }}>
                        <Chip
                            label="All topics"
                            onClick={() => setSelectedMathTopic(null)}
                            color={selectedMathTopic === null ? "primary" : "default"}
                            variant={selectedMathTopic === null ? "filled" : "outlined"}
                            sx={{ fontWeight: 600 }}
                        />
                        {getTopicsForSubject("maths").map((topic) => (
                            <Chip
                                key={topic.key}
                                label={topic.label}
                                onClick={() => setSelectedMathTopic(topic.key)}
                                color={selectedMathTopic === topic.key ? "primary" : "default"}
                                variant={selectedMathTopic === topic.key ? "filled" : "outlined"}
                                sx={{ fontWeight: 600 }}
                            />
                        ))}
                    </Box>
                )}
                {selectedSubject.key === "english" && (
                    <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", gap: 1 }}>
                        <Chip
                            label="All topics"
                            onClick={() => setSelectedEnglishTopic(null)}
                            color={selectedEnglishTopic === null ? "primary" : "default"}
                            variant={selectedEnglishTopic === null ? "filled" : "outlined"}
                            sx={{ fontWeight: 600 }}
                        />
                        {getTopicsForSubject("english").map((topic) => (
                            <Chip
                                key={topic.key}
                                label={topic.label}
                                onClick={() => setSelectedEnglishTopic(topic.key)}
                                color={selectedEnglishTopic === topic.key ? "primary" : "default"}
                                variant={selectedEnglishTopic === topic.key ? "filled" : "outlined"}
                                sx={{ fontWeight: 600 }}
                            />
                        ))}
                    </Box>
                )}
                {selectedSubject.key === "verbal-reasoning" && (
                    <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", gap: 1 }}>
                        <Chip
                            label="All topics"
                            onClick={() => setSelectedVerbalReasoningTopic(null)}
                            color={selectedVerbalReasoningTopic === null ? "primary" : "default"}
                            variant={selectedVerbalReasoningTopic === null ? "filled" : "outlined"}
                            sx={{ fontWeight: 600 }}
                        />
                        {getTopicsForSubject("verbal-reasoning").map((topic) => (
                            <Chip
                                key={topic.key}
                                label={topic.label}
                                onClick={() => setSelectedVerbalReasoningTopic(topic.key)}
                                color={selectedVerbalReasoningTopic === topic.key ? "primary" : "default"}
                                variant={selectedVerbalReasoningTopic === topic.key ? "filled" : "outlined"}
                                sx={{ fontWeight: 600 }}
                            />
                        ))}
                    </Box>
                )}
                {selectedSubject.key === "non-verbal-reasoning" && (
                    <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", gap: 1 }}>
                        <Chip
                            label="All topics"
                            onClick={() => setSelectedNonVerbalReasoningTopic(null)}
                            color={selectedNonVerbalReasoningTopic === null ? "primary" : "default"}
                            variant={selectedNonVerbalReasoningTopic === null ? "filled" : "outlined"}
                            sx={{ fontWeight: 600 }}
                        />
                        {getTopicsForSubject("non-verbal-reasoning").map((topic) => (
                            <Chip
                                key={topic.key}
                                label={topic.label}
                                onClick={() => setSelectedNonVerbalReasoningTopic(topic.key)}
                                color={selectedNonVerbalReasoningTopic === topic.key ? "primary" : "default"}
                                variant={selectedNonVerbalReasoningTopic === topic.key ? "filled" : "outlined"}
                                sx={{ fontWeight: 600 }}
                            />
                        ))}
                    </Box>
                )}

                {!subjectGames.length && (
                    <Paper
                        sx={{
                            p: 4,
                            borderRadius: 4,
                            textAlign: 'center',
                            backgroundColor: '#f9fafb',
                            border: '1px dashed #e5e7eb',
                            mb: 2,
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                            Games coming soon
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#374151' }}>
                            We are working on adding exciting interactive games for {selectedSubject.label}.
                        </Typography>
                    </Paper>
                )}

                <Grid container spacing={3}>
                    {selectedSubject.key === "maths" && (
                        (() => {
                            const topic = selectedMathTopic ? getTopicByKey("maths", selectedMathTopic) : null;
                            // Mixed Math Challenge only under "All topics"; never under specific topics
                            const show = (gameKey) => {
                                if (gameKey === "math-mixed") return !topic;
                                if (!topic) return true;
                                return topic.games.includes(gameKey);
                            };

                            return (
                                <>
                            {/* Addition */}
                            {show("math-add") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
                                }}>
                                    <CardContent
                                        sx={{
                                            textAlign: 'center',
                                            p: { xs: 1.5, sm: 2, md: 3 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <CalculateIcon sx={{ fontSize: 72, color: '#2e7d32', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Addition Adventure</Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ mb: 2, minHeight: 60 }}
                                        >
                                            Focus only on addition questions to build confidence with sums.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => setDifficultyPickerFor('math-add')}
                                            sx={{
                                                mt: 'auto',
                                                borderRadius: 999,
                                                px: 4,
                                                py: 1.2,
                                                fontSize: '1rem',
                                                fontWeight: 700,
                                                background: '#2e7d32',
                                            }}
                                        >
                                            Play Now
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Multiplication */}
                            {show("math-sub") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)'
                                }}>
                                    <CardContent
                                        sx={{
                                            textAlign: 'center',
                                            p: { xs: 1.5, sm: 2, md: 3 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <CalculateIcon sx={{ fontSize: 72, color: '#ef6c00', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Subtraction Sprint</Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ mb: 2, minHeight: 60 }}
                                        >
                                            Quick-fire subtraction questions to sharpen your number sense.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => setDifficultyPickerFor('math-sub')}
                                            sx={{
                                                mt: 'auto',
                                                borderRadius: 999,
                                                px: 4,
                                                py: 1.2,
                                                fontSize: '1rem',
                                                fontWeight: 700,
                                                background: '#ef6c00',
                                            }}
                                        >
                                            Play Now
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Division */}
                            {show("math-mul") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)'
                                }}>
                                    <CardContent
                                        sx={{
                                            textAlign: 'center',
                                            p: { xs: 1.5, sm: 2, md: 3 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <CalculateIcon sx={{ fontSize: 72, color: '#0277bd', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Multiplication Mania</Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ mb: 2, minHeight: 60 }}
                                        >
                                            Practice your times tables with multiplication-only questions.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => setDifficultyPickerFor('math-mul')}
                                            sx={{
                                                mt: 'auto',
                                                borderRadius: 999,
                                                px: 4,
                                                py: 1.2,
                                                fontSize: '1rem',
                                                fontWeight: 700,
                                                background: '#0277bd',
                                            }}
                                        >
                                            Play Now
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("math-div") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)'
                                }}>
                                    <CardContent
                                        sx={{
                                            textAlign: 'center',
                                            p: { xs: 1.5, sm: 2, md: 3 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <CalculateIcon sx={{ fontSize: 72, color: '#6a1b9a', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Division Dash</Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ mb: 2, minHeight: 60 }}
                                        >
                                            Solve division problems with whole-number answers.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => setDifficultyPickerFor('math-div')}
                                            sx={{
                                                mt: 'auto',
                                                borderRadius: 999,
                                                px: 4,
                                                py: 1.2,
                                                fontSize: '1rem',
                                                fontWeight: 700,
                                                background: '#6a1b9a',
                                            }}
                                        >
                                            Play Now
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Arithmetic & Operations: Long mult/div, BODMAS, Powers of 10, Word problems */}
                            {show("long-mult-div") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #ede7f6 0%, #d1c4e9 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <FunctionsIcon sx={{ fontSize: 72, color: '#5e35b1', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Long Multiplication & Division</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Larger calculations with visual grids and bus-stop layout.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('long-mult-div')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#5e35b1' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("bodmas") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <AccountTreeIcon sx={{ fontSize: 72, color: '#3f51b5', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>BODMAS / Order of Operations</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Brackets first, then × ÷, then + −. Practise the order.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('bodmas')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#3f51b5' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("powers-of-10") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <PowerIcon sx={{ fontSize: 72, color: '#0288d1', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Powers of 10</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Multiply and divide by 10, 100, 1000. Place value shifting.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('powers-of-10')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#0288d1' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("word-problems") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <DescriptionIcon sx={{ fontSize: 72, color: '#7b1fa2', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Arithmetic Word Problems</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Apply add, subtract, multiply and divide in real situations.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('word-problems')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#7b1fa2' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Number & Place Value games */}
                            {show("number-read-compare") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <PinIcon sx={{ fontSize: 72, color: '#3949ab', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Read & Compare</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Reading, writing, comparing and ordering large numbers with symbols.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('number-read-compare')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#3949ab' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("place-value-rounding") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <TableChartIcon sx={{ fontSize: 72, color: '#1565c0', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Place Value & Rounding</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Place value charts, rounding and estimation with number lines.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('place-value-rounding')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#1565c0' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("prime-factors-multiples") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <FilterListIcon sx={{ fontSize: 72, color: '#2e7d32', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Primes, Factors & Multiples</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Prime numbers, factors and multiples with visual grids.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('prime-factors-multiples')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#2e7d32' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("square-cube") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <ViewModuleIcon sx={{ fontSize: 72, color: '#e65100', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Square & Cube Numbers</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Visual grids and cube diagrams for ² and ³.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('square-cube')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#e65100' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("negative-sequences") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <TrendingDownIcon sx={{ fontSize: 72, color: '#c2185b', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Negative Numbers & Sequences</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Number line and sequence patterns with negative numbers.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('negative-sequences')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#c2185b' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Time Telling Challenge */}
                            {show("clock-mcq") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)'
                                }}>
                                    <CardContent
                                        sx={{
                                            textAlign: 'center',
                                            p: { xs: 1.5, sm: 2, md: 3 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <CalculateIcon sx={{ fontSize: 72, color: '#00838f', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Time Telling Challenge</Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ mb: 2, minHeight: 60 }}
                                        >
                                            Read analogue clocks and match the correct time.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => setDifficultyPickerFor('clock-mcq')}
                                            sx={{
                                                mt: 'auto',
                                                borderRadius: 999,
                                                px: 4,
                                                py: 1.2,
                                                fontSize: '1rem',
                                                fontWeight: 700,
                                                background: '#00838f',
                                                '&:hover': { background: '#006064' },
                                            }}
                                        >
                                            Play Now
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Metric Conversion & Measurement Problems */}
                            {show("metric-conversion") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <SwapHorizIcon sx={{ fontSize: 72, color: '#2e7d32', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Metric Conversion</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Convert between length, mass, capacity and time units.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('metric-conversion')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#2e7d32' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("measurement-problems") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <TimelineIcon sx={{ fontSize: 72, color: '#0277bd', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Measurement Problems</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Length, mass, capacity and time in real situations.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('measurement-problems')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#0277bd' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Percentage Quest */}
                            {show("percent-quest") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #fffde7 0%, #fff9c4 100%)'
                                }}>
                                    <CardContent
                                        sx={{
                                            textAlign: 'center',
                                            p: { xs: 1.5, sm: 2, md: 3 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <CalculateIcon sx={{ fontSize: 72, color: '#f9a825', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Percentage Quest</Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ mb: 2, minHeight: 60 }}
                                        >
                                            Solve percentage problems with colourful visual hints.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => setDifficultyPickerFor('percent-quest')}
                                            sx={{
                                                mt: 'auto',
                                                borderRadius: 999,
                                                px: 4,
                                                py: 1.2,
                                                fontSize: '1rem',
                                                fontWeight: 700,
                                                background: '#f9a825',
                                                '&:hover': { background: '#f57f17' },
                                            }}
                                        >
                                            Play Now
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Fractions, Decimals & Percentages: equivalent, convert, fraction ops */}
                            {show("equivalent-fractions") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <AspectRatioIcon sx={{ fontSize: 72, color: '#e65100', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Equivalent Fractions</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Simplify fractions to lowest terms with fraction bar visuals.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('equivalent-fractions')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#e65100' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("fraction-decimal-percent") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <SwapHorizIcon sx={{ fontSize: 72, color: '#2e7d32', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Fraction ↔ Decimal ↔ %</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Convert between fractions, decimals and percentages.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('fraction-decimal-percent')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#2e7d32' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("fraction-ops") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <PieChartIcon sx={{ fontSize: 72, color: '#0277bd', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Fraction Operations</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Add, subtract, multiply and divide fractions. Simplify your answer.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('fraction-ops')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#0277bd' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Ratio & Proportion games */}
                            {show("ratio-notation") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <CompareArrowsIcon sx={{ fontSize: 72, color: '#c2185b', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Ratio Notation</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Simplify ratios to simplest form with ratio bar visuals.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('ratio-notation')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#c2185b' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("ratio-proportion-problems") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <BalanceIcon sx={{ fontSize: 72, color: '#3949ab', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Ratio & Proportion</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Solve problems using ratios: cost, recipes, weight and more.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('ratio-proportion-problems')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#3949ab' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("sharing-in-ratio") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <ShareIcon sx={{ fontSize: 72, color: '#ef6c00', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Sharing in Ratio</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Share quantities in a given ratio. Visual split diagrams.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('sharing-in-ratio')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#ef6c00' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Algebra Basics games */}
                            {show("algebra-notation") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <TagIcon sx={{ fontSize: 72, color: '#2e7d32', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Algebraic Notation</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Match words to expressions: 3x, a+5, y−4 and more.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('algebra-notation')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#2e7d32' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("algebra-equations") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <EqualizerIcon sx={{ fontSize: 72, color: '#1565c0', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Linear Equations</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Solve for x: one-step and two-step equations.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('algebra-equations')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#1565c0' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Geometry & Shape games */}
                            {show("geometry-2d-3d") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4, transition: 'transform 0.3s ease, box-shadow 0.3s ease', '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' }, background: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)' }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <CategoryIcon sx={{ fontSize: 72, color: '#0288d1', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>2D & 3D Shapes</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>Identify shapes, sides, faces and properties.</Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('geometry-2d-3d')} sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#0288d1' }}>Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("geometry-angles") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4, transition: 'transform 0.3s ease, box-shadow 0.3s ease', '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' }, background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)' }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <StraightenIcon sx={{ fontSize: 72, color: '#f9a825', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Angles</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>Types, measuring, and calculating angles on straight lines.</Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('geometry-angles')} sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#f9a825' }}>Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("geometry-symmetry") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4, transition: 'transform 0.3s ease, box-shadow 0.3s ease', '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' }, background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)' }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <FlipIcon sx={{ fontSize: 72, color: '#7b1fa2', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Symmetry</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>Reflection and rotational symmetry of shapes.</Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('geometry-symmetry')} sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#7b1fa2' }}>Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("geometry-perimeter-area") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4, transition: 'transform 0.3s ease, box-shadow 0.3s ease', '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' }, background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <SquareFootIcon sx={{ fontSize: 72, color: '#2e7d32', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Perimeter & Area</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>Rectangles: perimeter and area calculations.</Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('geometry-perimeter-area')} sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#2e7d32' }}>Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("geometry-volume") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4, transition: 'transform 0.3s ease, box-shadow 0.3s ease', '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' }, background: 'linear-gradient(135deg, #eceff1 0%, #cfd8dc 100%)' }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <ViewInArIcon sx={{ fontSize: 72, color: '#546e7a', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Volume</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>Cubes and cuboids: volume of simple solids.</Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('geometry-volume')} sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#546e7a' }}>Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Number Patterns */}
                            {show("pattern-quest") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #ede7f6 0%, #d1c4e9 100%)'
                                }}>
                                    <CardContent
                                        sx={{
                                            textAlign: 'center',
                                            p: { xs: 1.5, sm: 2, md: 3 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <CalculateIcon sx={{ fontSize: 72, color: '#5e35b1', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Number Patterns</Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ mb: 2, minHeight: 60 }}
                                        >
                                            Spot the rule and complete the number patterns.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => setDifficultyPickerFor('pattern-quest')}
                                            sx={{
                                                mt: 'auto',
                                                borderRadius: 999,
                                                px: 4,
                                                py: 1.2,
                                                fontSize: '1rem',
                                                fontWeight: 700,
                                                background: '#5e35b1',
                                                '&:hover': { background: '#4527a0' },
                                            }}
                                        >
                                            Play Now
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Pie Chart Puzzles */}
                            {show("pie-chart") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
                                }}>
                                    <CardContent
                                        sx={{
                                            textAlign: 'center',
                                            p: { xs: 1.5, sm: 2, md: 3 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <CalculateIcon sx={{ fontSize: 72, color: '#2e7d32', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Pie Chart Puzzles</Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ mb: 2, minHeight: 60 }}
                                        >
                                            Read simple pie charts and choose the right answer.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => setDifficultyPickerFor('pie-chart')}
                                            sx={{
                                                mt: 'auto',
                                                borderRadius: 999,
                                                px: 4,
                                                py: 1.2,
                                                fontSize: '1rem',
                                                fontWeight: 700,
                                                background: '#2e7d32',
                                                '&:hover': { background: '#1b5e20' },
                                            }}
                                        >
                                            Play Now
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Mean, Median, Mode & Probability */}
                            {show("mean-median-mode") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <BarChartIcon sx={{ fontSize: 72, color: '#e65100', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Mean, Median & Mode</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Calculate averages from data lists. Tables and number sets.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('mean-median-mode')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#e65100' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {show("probability-basics") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <CasinoIcon sx={{ fontSize: 72, color: '#7b1fa2', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Probability Basics</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Chance of simple events: dice, coins, bags and spinners.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('probability-basics')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#7b1fa2' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Multi-Step Reasoning */}
                            {show("multi-step-reasoning") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <LightbulbIcon sx={{ fontSize: 72, color: '#2e7d32', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Multi-Step Reasoning</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Analyse multi-step word problems. Apply logic across two or more steps.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('multi-step-reasoning')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#2e7d32' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Logic & Reasoning */}
                            {show("logic-reasoning") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)'
                                }}>
                                    <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                                        <PsychologyIcon sx={{ fontSize: 72, color: '#f57c00', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Logic & Reasoning</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                            Work backwards, find missing numbers and apply reasoning.
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => setDifficultyPickerFor('logic-reasoning')}
                                            sx={{ mt: 'auto', borderRadius: 999, px: 4, py: 1.2, fontSize: '1rem', fontWeight: 700, background: '#f57c00' }}
                                        >Play Now</Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                            {/* Mixed Math Challenge (last maths game) */}
                            {show("math-mixed") && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 4,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' },
                                    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                                }}>
                                    <CardContent
                                        sx={{
                                            textAlign: 'center',
                                            p: { xs: 1.5, sm: 2, md: 3 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <CalculateIcon sx={{ fontSize: 72, color: '#1976d2', mb: 1.5 }} />
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>Mixed Math Challenge</Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ mb: 2, minHeight: 60 }}
                                        >
                                            Mix of addition, subtraction, multiplication and division problems.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => setDifficultyPickerFor('math-mixed')}
                                            sx={{
                                                mt: 'auto',
                                                borderRadius: 999,
                                                px: 4,
                                                py: 1.2,
                                                fontSize: '1rem',
                                                fontWeight: 700,
                                                background: '#1976d2',
                                            }}
                                        >
                                            Play Now
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            )}
                                </>
                            );
                        })()
                    )}
                    {selectedSubject.key === "english" && (
                        <>
                            {/* English MCQ topic games */}
                            {(() => {
                                const topic = selectedEnglishTopic ? getTopicByKey("english", selectedEnglishTopic) : null;
                                const allowedKeys = topic ? topic.games : ENGLISH_GAME_CARDS.map((g) => g.gameKey);
                                const visibleCards = ENGLISH_GAME_CARDS.filter((card) => allowedKeys.includes(card.gameKey));
                                return visibleCards.map((card) => (
                                    <Grid item xs={12} sm={6} md={3} key={card.gameKey}>
                                        <Card
                                            sx={{
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                borderRadius: 4,
                                                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                                "&:hover": {
                                                    transform: "translateY(-8px)",
                                                    boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
                                                },
                                                background: card.gradient,
                                            }}
                                        >
                                            <CardContent
                                                sx={{
                                                    textAlign: "center",
                                                    p: { xs: 1.5, sm: 2, md: 3 },
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    height: "100%",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 72,
                                                        height: 72,
                                                        borderRadius: 2,
                                                        bgcolor: "rgba(255,255,255,0.5)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        mb: 1.5,
                                                    }}
                                                >
                                                    {(() => { const IconComp = ENGLISH_ICON_MAP[card.iconKey] || EnglishIcon; return <IconComp sx={{ fontSize: 40, color: card.iconColor }} /> })()}
                                                </Box>
                                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                                    {card.title}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    color="text.secondary"
                                                    sx={{ mb: 2, minHeight: 60 }}
                                                >
                                                    {card.description}
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    size="large"
                                                    onClick={() => setDifficultyPickerFor(card.gameKey)}
                                                    sx={{
                                                        mt: "auto",
                                                        borderRadius: 999,
                                                        px: 4,
                                                        py: 1.2,
                                                        fontSize: "1rem",
                                                        fontWeight: 700,
                                                        background: card.buttonBg,
                                                    }}
                                                >
                                                    Play Now
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ));
                            })()}
                            {/* Unscramble vocabulary game */}
                            {subjectGames.includes("english") && (
                                <Grid item xs={12} sm={6} md={3}>
                                    <Card
                                        sx={{
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            borderRadius: 4,
                                            transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                            "&:hover": {
                                                transform: "translateY(-8px)",
                                                boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
                                            },
                                            background: "linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)",
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                textAlign: "center",
                                                p: { xs: 1.5, sm: 2, md: 3 },
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                height: "100%",
                                            }}
                                        >
                                            <EnglishIcon sx={{ fontSize: 72, color: "#c2185b", mb: 1.5 }} />
                                            <Typography variant="h5" fontWeight="bold" gutterBottom>
                                                Vocabulary Rush (Unscramble)
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                color="text.secondary"
                                                sx={{ mb: 2, minHeight: 60 }}
                                            >
                                                Unscramble words and expand your vocabulary with help from Datamuse API.
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                size="large"
                                                sx={{
                                                    mt: "auto",
                                                    borderRadius: 999,
                                                    px: 4,
                                                    py: 1.2,
                                                    fontSize: "1rem",
                                                    fontWeight: 700,
                                                    background: "#c2185b",
                                                }}
                                                onClick={() => setDifficultyPickerFor("english")}
                                            >
                                                Play Now
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}
                        </>
                    )}
                    {selectedSubject.key === "verbal-reasoning" && (
                        (() => {
                            const topic = selectedVerbalReasoningTopic ? getTopicByKey("verbal-reasoning", selectedVerbalReasoningTopic) : null;
                            const allowedKeys = topic ? topic.games : VERBAL_REASONING_GAME_CARDS.map((g) => g.gameKey);
                            const visibleCards = VERBAL_REASONING_GAME_CARDS.filter((card) => allowedKeys.includes(card.gameKey));
                            return visibleCards.map((card) => (
                                <Grid item xs={12} sm={6} md={3} key={card.gameKey}>
                                    <Card
                                        sx={{
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            borderRadius: 4,
                                            transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                            "&:hover": {
                                                transform: "translateY(-8px)",
                                                boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
                                            },
                                            background: card.gradient,
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                textAlign: "center",
                                                p: { xs: 1.5, sm: 2, md: 3 },
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                height: "100%",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 72,
                                                    height: 72,
                                                    borderRadius: 2,
                                                    bgcolor: "rgba(255,255,255,0.5)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    mb: 1.5,
                                                }}
                                            >
                                                {(function() { const Icon = VERBAL_REASONING_ICON_MAP[card.gameKey] || EnglishIcon; return <Icon sx={{ fontSize: 40, color: card.iconColor }} />; })()}
                                            </Box>
                                            <Typography variant="h5" fontWeight="bold" gutterBottom>
                                                {card.title}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                color="text.secondary"
                                                sx={{ mb: 2, minHeight: 60 }}
                                            >
                                                {card.description}
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                size="large"
                                                onClick={() => setDifficultyPickerFor(card.gameKey)}
                                                sx={{
                                                    mt: "auto",
                                                    borderRadius: 999,
                                                    px: 4,
                                                    py: 1.2,
                                                    fontSize: "1rem",
                                                    fontWeight: 700,
                                                    background: card.buttonBg,
                                                }}
                                            >
                                                Play Now
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ));
                        })()
                    )}
                    {selectedSubject.key === "non-verbal-reasoning" && (
                        (() => {
                            const topic = selectedNonVerbalReasoningTopic ? getTopicByKey("non-verbal-reasoning", selectedNonVerbalReasoningTopic) : null;
                            const allowedKeys = topic ? topic.games : NON_VERBAL_REASONING_GAME_CARDS.map((g) => g.gameKey);
                            const visibleCards = NON_VERBAL_REASONING_GAME_CARDS.filter((card) => allowedKeys.includes(card.gameKey));
                            return visibleCards.map((card) => (
                                <Grid item xs={12} sm={6} md={3} key={card.gameKey}>
                                    <Card
                                        sx={{
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            borderRadius: 4,
                                            transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                            "&:hover": {
                                                transform: "translateY(-8px)",
                                                boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
                                            },
                                            background: card.gradient,
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                textAlign: "center",
                                                p: { xs: 1.5, sm: 2, md: 3 },
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                height: "100%",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 72,
                                                    height: 72,
                                                    borderRadius: 2,
                                                    bgcolor: "rgba(255,255,255,0.5)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    mb: 1.5,
                                                }}
                                            >
                                                <ShapeBlock shape={{ t: "s", f: 1 }} size={40} />
                                            </Box>
                                            <Typography variant="h5" fontWeight="bold" gutterBottom>
                                                {card.title}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                color="text.secondary"
                                                sx={{ mb: 2, minHeight: 60 }}
                                            >
                                                {card.description}
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                size="large"
                                                onClick={() => setDifficultyPickerFor(card.gameKey)}
                                                sx={{
                                                    mt: "auto",
                                                    borderRadius: 999,
                                                    px: 4,
                                                    py: 1.2,
                                                    fontSize: "1rem",
                                                    fontWeight: 700,
                                                    background: card.buttonBg,
                                                }}
                                            >
                                                Play Now
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ));
                        })()
                    )}
                    {/* Flash Cards entry for each subject (only under All topics) */}
                    {selectedSubject.key === "maths" && !selectedMathTopic && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: 4,
                                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                    "&:hover": {
                                        transform: "translateY(-8px)",
                                        boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
                                    },
                                    background: "linear-gradient(135deg, #e0f7fa 0%, #e3f2fd 100%)",
                                }}
                            >
                                <CardContent
                                    sx={{
                                        textAlign: "center",
                                        p: { xs: 1.5, sm: 2, md: 3 },
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        height: "100%",
                                    }}
                                >
                                    <CalculateIcon sx={{ fontSize: 72, color: "#2ecc71", mb: 1.5 }} />
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                                        Maths Vocabulary
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{ mb: 2, minHeight: 60 }}
                                    >
                                        Practise key maths vocabulary and topic words from clues.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => navigate("/student/vocabulary/maths")}
                                        sx={{
                                            mt: "auto",
                                            borderRadius: 999,
                                            px: 4,
                                            py: 1.2,
                                            fontSize: "1rem",
                                            fontWeight: 700,
                                            background: "#2ecc71",
                                        }}
                                    >
                                        Play Now
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                    {selectedSubject.key === "english" && !selectedEnglishTopic && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: 4,
                                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                    "&:hover": {
                                        transform: "translateY(-8px)",
                                        boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
                                    },
                                    background: "linear-gradient(135deg, #fce4ec 0%, #fff3e0 100%)",
                                }}
                            >
                                <CardContent
                                    sx={{
                                        textAlign: "center",
                                        p: { xs: 1.5, sm: 2, md: 3 },
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        height: "100%",
                                    }}
                                >
                                    <EnglishIcon sx={{ fontSize: 72, color: "#2ecc71", mb: 1.5 }} />
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                                        English Vocabulary
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{ mb: 2, minHeight: 60 }}
                                    >
                                        Practise tricky spellings and vocabulary using word clues.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => navigate("/student/vocabulary/english")}
                                        sx={{
                                            mt: "auto",
                                            borderRadius: 999,
                                            px: 4,
                                            py: 1.2,
                                            fontSize: "1rem",
                                            fontWeight: 700,
                                            background: "#2ecc71",
                                        }}
                                    >
                                        Play Now
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                    {selectedSubject.key === "verbal-reasoning" && !selectedVerbalReasoningTopic && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: 4,
                                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                    "&:hover": {
                                        transform: "translateY(-8px)",
                                        boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
                                    },
                                    background: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
                                }}
                            >
                                <CardContent
                                    sx={{
                                        textAlign: "center",
                                        p: { xs: 1.5, sm: 2, md: 3 },
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        height: "100%",
                                    }}
                                >
                                    <PsychologyIcon sx={{ fontSize: 72, color: "#2ecc71", mb: 1.5 }} />
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                                        Verbal Reasoning Vocabulary
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{ mb: 2, minHeight: 60 }}
                                    >
                                        Practise word relationships, synonyms and antonyms from clues.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => navigate("/student/vocabulary/verbal-reasoning")}
                                        sx={{
                                            mt: "auto",
                                            borderRadius: 999,
                                            px: 4,
                                            py: 1.2,
                                            fontSize: "1rem",
                                            fontWeight: 700,
                                            background: "#2ecc71",
                                        }}
                                    >
                                        Play Now
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                    {selectedSubject.key === "non-verbal-reasoning" && !selectedNonVerbalReasoningTopic && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: 4,
                                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                    "&:hover": {
                                        transform: "translateY(-8px)",
                                        boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
                                    },
                                    background: "linear-gradient(135deg, #fff3e0 0%, #e3f2fd 100%)",
                                }}
                            >
                                <CardContent
                                    sx={{
                                        textAlign: "center",
                                        p: { xs: 1.5, sm: 2, md: 3 },
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        height: "100%",
                                    }}
                                >
                                    <ShapeBlock shape={{ t: "s", f: 1 }} size={40} />
                                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 1.5 }}>
                                        Non-Verbal Vocabulary
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{ mb: 2, minHeight: 60 }}
                                    >
                                        Practise key shape and pattern vocabulary using quick clues.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => navigate("/student/vocabulary/non-verbal-reasoning")}
                                        sx={{
                                            mt: "auto",
                                            borderRadius: 999,
                                            px: 4,
                                            py: 1.2,
                                            fontSize: "1rem",
                                            fontWeight: 700,
                                            background: "#2ecc71",
                                        }}
                                    >
                                        Play Now
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 4, position: 'relative', minHeight: 600 }}>
                {/* Stats Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Button
                        startIcon={<RefreshIcon />}
                        onClick={() => {
                            if (gameActive) {
                                submitGameScore("quit");
                            }
                            setSelectedGame(null);
                        }}
                        sx={{ color: '#374151', border: '1px solid #ddd', borderRadius: 2 }}
                    >
                        Quit Game
                    </Button>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        <Chip
                            icon={<SuccessIcon style={{ color: '#10b981' }} />}
                            label={`Score: ${score}`}
                            sx={{ fontWeight: 'bold', fontSize: '1.2rem', p: 1, backgroundColor: '#f0fdf4', border: '1px solid #10b981', color: '#065f46' }}
                        />
                        <Chip
                            icon={<TimerIcon style={{ color: timeLeft < 10 ? '#ef4444' : '#1e40af' }} />}
                            label={`Time: ${timeLeft}s`}
                            sx={{ fontWeight: 'bold', fontSize: '1.2rem', p: 1, backgroundColor: timeLeft < 10 ? '#fef2f2' : '#eff6ff', border: `1px solid ${timeLeft < 10 ? '#ef4444' : '#1e40af'}`, color: timeLeft < 10 ? '#991b1b' : '#1e40af' }}
                        />
                    </Box>
                </Box>

                <Divider sx={{ mb: 4 }} />

                {!gameActive ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <TrophyIcon sx={{ fontSize: 100, color: '#fbbf24', mb: 2 }} />
                        <Typography variant="h3" fontWeight="bold" gutterBottom>Game Over!</Typography>
                        <Typography variant="h5" sx={{ mb: 4 }}>Your Final Score: <Box component="span" sx={{ color: '#10b981', fontWeight: 'bold' }}>{score}</Box></Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => startGame(selectedGame)}
                            sx={{ borderRadius: 8, px: 6, py: 2, fontSize: '1.2rem', mr: 2, background: 'linear-gradient(45deg, #10b981, #059669)' }}
                        >
                            Play Again
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={() => setSelectedGame(null)}
                            sx={{ borderRadius: 8, px: 6, py: 2, fontSize: '1.2rem' }}
                        >
                            Change Game
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center' }}>
                        {currentProblem?.hint && (
                            <Chip label={currentProblem.hint} sx={{ mb: 1, fontWeight: 600, bgcolor: '#f3f4f6' }} />
                        )}

                        {loading ? (
                            <Box sx={{ py: 10 }}>
                                <LinearProgress sx={{ borderRadius: 5, height: 10, width: '60%', mx: 'auto' }} />
                                <Typography sx={{ mt: 2 }}>Preparing your challenge...</Typography>
                            </Box>
                        ) : (
                            <Box>
                                {isMathGame ? (
                                    <Box
                                        component="form"
                                        onSubmit={(e) => { e.preventDefault(); handleAnswerSubmit(); }}
                                        onKeyDownCapture={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (!feedback) handleAnswerSubmit();
                                            }
                                        }}
                                    >
                                        <GameQuestionRich
                                            variant="h1"
                                            fontWeight="bold"
                                            value={currentProblem?.question}
                                            sx={{
                                                my: { xs: 4, md: 5 },
                                                color: '#1e293b',
                                                fontSize: { xs: '3rem', md: '4.2rem' },
                                                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI"',
                                                letterSpacing: 2,
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 2,
                                            }}
                                        >
                                            <TextField
                                                value={userAnswer}
                                                placeholder="Enter your answer"
                                                variant="outlined"
                                                size="small"
                                                autoComplete="off"
                                                readOnly
                                                disabled={!!feedback}
                                                sx={{
                                                    width: '100%',
                                                    minWidth: 260,
                                                    maxWidth: 320,
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 999,
                                                        fontSize: '1.8rem',
                                                        textAlign: 'center',
                                                        backgroundColor: '#f5f7ff',
                                                        border: '2px solid #d0d4ff',
                                                        paddingY: 0.5,
                                                    },
                                                }}
                                                inputProps={{ style: { textAlign: 'center' } }}
                                            />
                                            <MathKeyboard />
                                        </Box>
                                    </Box>
                                ) : isClockGame ? (
                                    <Box sx={{ py: 2 }}>
                                        {(currentProblem?.mode === "read" || (!currentProblem?.mode && currentProblem?.options)) && (
                                            <>
                                                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: "#1976d2" }}>
                                                    Please choose the correct time
                                                </Typography>
                                                <Box sx={{ mb: 4 }}>
                                                    <AnalogClock
                                                        hours={currentProblem?.hours}
                                                        minutes={currentProblem?.minutes}
                                                        size={220}
                                                    />
                                                </Box>
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 320, mx: "auto" }}>
                                                    {currentProblem?.options?.map((opt) => (
                                                        <Button
                                                            key={opt}
                                                            variant="outlined"
                                                            size="large"
                                                            onClick={() => handleAnswerSubmit(opt)}
                                                            disabled={!!feedback}
                                                            sx={{
                                                                py: 1.5,
                                                                borderRadius: 3,
                                                                fontSize: "1.25rem",
                                                                fontWeight: 700,
                                                                borderWidth: 2,
                                                                "&:hover": { borderWidth: 2 },
                                                            }}
                                                        >
                                                            {opt}
                                                        </Button>
                                                    ))}
                                                </Box>
                                            </>
                                        )}
                                        {currentProblem?.mode === "match" && (
                                            <>
                                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "#1976d2" }}>
                                                    Choose the correct clock
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        mb: 3,
                                                        p: 2,
                                                        borderRadius: 3,
                                                        bgcolor: "#e8f5e9",
                                                        border: "2px solid #4caf50",
                                                        display: "inline-block",
                                                        mx: "auto",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    <Typography variant="h4" fontWeight="bold" color="#2e7d32">
                                                        {formatTime(currentProblem?.targetHours, currentProblem?.targetMinutes)}
                                                    </Typography>
                                                </Box>
                                                <Grid container spacing={2} sx={{ maxWidth: 420, mx: "auto", justifyContent: "center" }}>
                                                    {currentProblem?.clockOptions?.map((opt, idx) => (
                                                        <Grid item xs={6} key={idx}>
                                                            <Box
                                                                onClick={() => !feedback && handleAnswerSubmit(String(idx))}
                                                                sx={{
                                                                    cursor: feedback ? "default" : "pointer",
                                                                    border: "3px solid",
                                                                    borderColor: feedback && currentProblem?.answer === String(idx) ? "#4caf50" : "#e0e0e0",
                                                                    borderRadius: 2,
                                                                    p: 1,
                                                                    "&:hover": feedback ? {} : { borderColor: "#1976d2", bgcolor: "#e3f2fd" },
                                                                }}
                                                            >
                                                                <AnalogClock hours={opt.hours} minutes={opt.minutes} size={140} />
                                                            </Box>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </>
                                        )}
                                        {currentProblem?.mode === "set" && (
                                            <>
                                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "#1976d2" }}>
                                                    Set time on clock by moving hour/minute hand
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        mb: 2,
                                                        p: 2,
                                                        borderRadius: 3,
                                                        bgcolor: "#fff8e1",
                                                        border: "2px solid #ffc107",
                                                        display: "inline-block",
                                                        mx: "auto",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    <Typography variant="h4" fontWeight="bold" color="#f57c00">
                                                        Set to: {formatTime(currentProblem?.targetHours, currentProblem?.targetMinutes)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 3 }}>
                                                    <InteractiveAnalogClock
                                                        hours={clockSetHours}
                                                        minutes={clockSetMinutes}
                                                        size={220}
                                                        onChange={(h, m) => {
                                                            setClockSetHours(h);
                                                            setClockSetMinutes(m);
                                                        }}
                                                    />
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    size="large"
                                                    onClick={() =>
                                                        handleAnswerSubmit(
                                                            formatTime(clockSetHours, clockSetMinutes)
                                                        )
                                                    }
                                                    disabled={!!feedback}
                                                    sx={{ py: 1.5, px: 4, borderRadius: 3, fontWeight: 700 }}
                                                >
                                                    Check
                                                </Button>
                                            </>
                                        )}
                                    </Box>
                                ) : isPercentGame ? (
                                    <Box sx={{ py: 2 }}>
                                        <GameQuestionRich variant="h6" value={currentProblem?.question} sx={{ mb: 3, fontWeight: 700, color: "#f9a825" }} />
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 320, mx: "auto" }}>
                                            {currentProblem?.options?.map((opt) => (
                                                <Button
                                                    key={opt}
                                                    variant="outlined"
                                                    size="large"
                                                    onClick={() => handleAnswerSubmit(opt)}
                                                    disabled={!!feedback}
                                                    sx={{
                                                        py: 1.5,
                                                        borderRadius: 3,
                                                        fontSize: "1.25rem",
                                                        fontWeight: 700,
                                                        borderWidth: 2,
                                                        "&:hover": { borderWidth: 2 },
                                                    }}
                                                >
                                                    {opt}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : isPatternGame ? (
                                    <Box sx={{ py: 2 }}>
                                        <GameQuestionRich variant="h6" value={currentProblem?.question} sx={{ mb: 2, fontWeight: 700, color: "#5e35b1" }} />
                                        <Typography variant="h4" sx={{ mb: 3, fontFamily: "monospace", letterSpacing: 2 }}>
                                            {currentProblem?.sequenceStr}
                                        </Typography>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 320, mx: "auto" }}>
                                            {currentProblem?.options?.map((opt) => (
                                                <Button
                                                    key={opt}
                                                    variant="outlined"
                                                    size="large"
                                                    onClick={() => handleAnswerSubmit(opt)}
                                                    disabled={!!feedback}
                                                    sx={{
                                                        py: 1.5,
                                                        borderRadius: 3,
                                                        fontSize: "1.25rem",
                                                        fontWeight: 700,
                                                        borderWidth: 2,
                                                        "&:hover": { borderWidth: 2 },
                                                    }}
                                                >
                                                    {opt}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : isNumberPlaceValueGame ? (
                                    <Box sx={{ py: 2 }}>
                                        <GameQuestionRich variant="h6" value={currentProblem?.question} sx={{ mb: 2, fontWeight: 700, color: "#3949ab" }} />
                                        {currentProblem?.visualType === "compare-grid" && currentProblem?.numbers && (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, mb: 3, flexWrap: "wrap" }}>
                                                <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#e8eaf6", border: "2px solid #3949ab", fontSize: "1.5rem", fontWeight: 700 }}>{currentProblem.numbers[0]}</Box>
                                                <Typography sx={{ fontSize: "2rem", fontWeight: 800, color: "#3949ab" }}>{currentProblem.symbol}</Typography>
                                                <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#e8eaf6", border: "2px solid #3949ab", fontSize: "1.5rem", fontWeight: 700 }}>{currentProblem.numbers[1]}</Box>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "place-value-chart" && currentProblem?.placeValueChart && (
                                            <Box sx={{ mb: 3, maxWidth: 400, mx: "auto" }}>
                                                <Table size="small" sx={{ border: "2px solid #1565c0", borderRadius: 2, overflow: "hidden" }}>
                                                    <TableHead>
                                                        <TableRow sx={{ bgcolor: "#bbdefb" }}>
                                                            <TableCell sx={{ fontWeight: 700 }}>Place</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 700 }}>Digit</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {currentProblem.placeValueChart.map((row, idx) => (
                                                            <TableRow key={idx} sx={{ bgcolor: row.highlighted ? "#fff9c4" : "#fff" }}>
                                                                <TableCell>{row.place}</TableCell>
                                                                <TableCell align="center" sx={{ fontWeight: row.highlighted ? 800 : 600 }}>{row.value}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                                <Typography variant="body2" sx={{ mt: 1, color: "#374151" }}>Number: {currentProblem.number}</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "square-grid" && currentProblem?.side !== undefined && (
                                            <Box sx={{ mb: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                                                <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${currentProblem.side}, 1fr)`, gap: 0.5, border: "2px solid #e65100", p: 1, borderRadius: 2 }}>
                                                    {Array.from({ length: currentProblem.side * currentProblem.side }).map((_, i) => (
                                                        <Box key={i} sx={{ width: 28, height: 28, bgcolor: "#ffe0b2", border: "1px solid #e65100", borderRadius: 0.5 }} />
                                                    ))}
                                                </Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{currentProblem.side} × {currentProblem.side} = ?</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "number-line-sequence" && currentProblem?.sequence && (
                                            <Typography variant="h4" sx={{ mb: 3, fontFamily: "monospace", letterSpacing: 2 }}>
                                                {currentProblem.sequence.join("  →  ")}
                                            </Typography>
                                        )}
                                        {currentProblem?.visualType === "factor-dots" && currentProblem?.number !== undefined && (
                                            <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "center" }}>
                                                {currentProblem.factors?.slice(0, 8).map((f, i) => (
                                                    <Chip key={i} label={f} size="small" sx={{ m: 0.5 }} />
                                                ))}
                                                <Typography variant="body2" sx={{ alignSelf: "center", ml: 1 }}>Factors of {currentProblem.number}</Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 320, mx: "auto" }}>
                                            {currentProblem?.options?.map((opt) => (
                                                <Button
                                                    key={opt}
                                                    variant="outlined"
                                                    size="large"
                                                    onClick={() => handleAnswerSubmit(opt)}
                                                    disabled={!!feedback}
                                                    sx={{ py: 1.5, borderRadius: 3, fontSize: "1.25rem", fontWeight: 700, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
                                                >
                                                    {opt}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : isStatisticsDataGame ? (
                                    <Box sx={{ py: 2 }}>
                                        <GameQuestionRich variant="h6" value={currentProblem?.question} sx={{ mb: 2, fontWeight: 700, color: "#e65100" }} />
                                        {currentProblem?.visualType === "data-list" && currentProblem?.values?.length > 0 && (
                                            <Box sx={{ mb: 3, p: 2, borderRadius: 3, bgcolor: "#fff3e0", border: "2px solid #ff9800", display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
                                                {currentProblem.values.map((v, i) => (
                                                    <Chip key={i} label={v} sx={{ fontWeight: 700, fontSize: "1rem" }} color="primary" variant="outlined" />
                                                ))}
                                            </Box>
                                        )}
                                        {currentProblem?.measure && (
                                            <Typography variant="body2" sx={{ mb: 2, color: "#374151" }}>
                                                Find the {currentProblem.measure}.
                                            </Typography>
                                        )}
                                        {currentProblem?.visualType === "probability" && (
                                            <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                                                <Chip label="P = favourable / total" size="small" sx={{ bgcolor: "#f3e5f5" }} />
                                            </Box>
                                        )}
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 320, mx: "auto" }}>
                                            {currentProblem?.options?.map((opt) => (
                                                <Button
                                                    key={opt}
                                                    variant="outlined"
                                                    size="large"
                                                    onClick={() => handleAnswerSubmit(opt)}
                                                    disabled={!!feedback}
                                                    sx={{ py: 1.5, borderRadius: 3, fontSize: "1.25rem", fontWeight: 700, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
                                                >
                                                    {opt}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : isProblemSolvingReasoningGame ? (
                                    <Box sx={{ py: 2 }}>
                                        {currentProblem?.visualType === "multi-step" && (
                                            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                                <LightbulbIcon sx={{ color: "#2e7d32", fontSize: 28 }} />
                                                <Typography variant="overline" sx={{ fontWeight: 700, color: "#2e7d32" }}>Multi-Step</Typography>
                                                <Chip label="Step 1 → Step 2" size="small" sx={{ bgcolor: "#e8f5e9" }} />
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "logic-work-back" && (
                                            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                                <PsychologyIcon sx={{ color: "#f57c00", fontSize: 28 }} />
                                                <Typography variant="overline" sx={{ fontWeight: 700, color: "#f57c00" }}>Work Backwards</Typography>
                                                <Chip label="? → result" size="small" sx={{ bgcolor: "#fff8e1" }} />
                                            </Box>
                                        )}
                                        <GameQuestionRich variant="h6" value={currentProblem?.question} sx={{ mb: 3, fontWeight: 700, color: "#1565c0" }} />
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 320, mx: "auto" }}>
                                            {currentProblem?.options?.map((opt) => (
                                                <Button
                                                    key={opt}
                                                    variant="outlined"
                                                    size="large"
                                                    onClick={() => handleAnswerSubmit(opt)}
                                                    disabled={!!feedback}
                                                    sx={{ py: 1.5, borderRadius: 3, fontSize: "1.25rem", fontWeight: 700, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
                                                >
                                                    {opt}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : isMeasurementGame ? (
                                    <Box sx={{ py: 2 }}>
                                        <GameQuestionRich variant="h6" value={currentProblem?.question} sx={{ mb: 2, fontWeight: 700, color: "#2e7d32" }} />
                                        {currentProblem?.visualType === "conversion-chart" && currentProblem?.value !== undefined && (
                                            <Box sx={{ mb: 3, p: 2, borderRadius: 3, bgcolor: "#e8f5e9", border: "2px solid #4caf50", display: "inline-flex", alignItems: "center", gap: 2 }}>
                                                <Typography variant="h5" sx={{ fontWeight: 700 }}>{currentProblem.value}</Typography>
                                                <SwapHorizIcon sx={{ fontSize: 32, color: "#2e7d32" }} />
                                                <Typography variant="body1" sx={{ color: "#374151" }}>→ Convert</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "measurement-problem" && (
                                            <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                                                <Chip label="length" size="small" sx={{ bgcolor: "#e3f2fd" }} />
                                                <Chip label="mass" size="small" sx={{ bgcolor: "#e8f5e9" }} />
                                                <Chip label="capacity" size="small" sx={{ bgcolor: "#e1f5fe" }} />
                                                <Chip label="time" size="small" sx={{ bgcolor: "#fff3e0" }} />
                                            </Box>
                                        )}
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 320, mx: "auto" }}>
                                            {currentProblem?.options?.map((opt) => (
                                                <Button
                                                    key={opt}
                                                    variant="outlined"
                                                    size="large"
                                                    onClick={() => handleAnswerSubmit(opt)}
                                                    disabled={!!feedback}
                                                    sx={{ py: 1.5, borderRadius: 3, fontSize: "1.25rem", fontWeight: 700, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
                                                >
                                                    {opt}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : isGeometryShapeGame ? (
                                    <Box sx={{ py: 2 }}>
                                        <GameQuestionRich variant="h6" value={currentProblem?.question} sx={{ mb: 2, fontWeight: 700, color: "#0288d1" }} />
                                        {currentProblem?.visualType === "shape-props" && currentProblem?.shapeRef && (
                                            <Box sx={{ mb: 3, display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
                                                {currentProblem.shapeRef === "cube" && (
                                                    <Box sx={{ width: 56, height: 56, border: "3px solid #0288d1", borderRadius: 1, bgcolor: "#e1f5fe" }} />
                                                )}
                                                {currentProblem.shapeRef === "hexagon" && (
                                                    <Box sx={{ width: 56, height: 48, border: "3px solid #0288d1", borderRadius: 0.5, bgcolor: "#e1f5fe", clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" }} />
                                                )}
                                                {currentProblem.shapeRef === "triangle" && (
                                                    <Box sx={{ width: 0, height: 0, borderLeft: "28px solid transparent", borderRight: "28px solid transparent", borderBottom: "48px solid #0288d1" }} />
                                                )}
                                                {currentProblem.shapeRef === "circle" && (
                                                    <Box sx={{ width: 48, height: 48, borderRadius: "50%", border: "3px solid #0288d1", bgcolor: "#e1f5fe" }} />
                                                )}
                                                {currentProblem.shapeRef === "square" && (
                                                    <Box sx={{ width: 48, height: 48, border: "3px solid #0288d1", borderRadius: 1, bgcolor: "#e1f5fe" }} />
                                                )}
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "angle-type" && currentProblem?.angleSize !== undefined && (
                                            <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: "#fff8e1", border: "2px solid #f9a825" }}>
                                                <Typography variant="h5" sx={{ fontWeight: 700 }}>{currentProblem.angleSize}°</Typography>
                                                <Typography variant="caption">What type of angle?</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "angle-calc" && currentProblem?.angle1 !== undefined && (
                                            <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: "#fff8e1", border: "2px solid #f9a825", display: "flex", alignItems: "center", gap: 2 }}>
                                                <Typography>{currentProblem.angle1}° + ? = {currentProblem.total}°</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "rectangle" && currentProblem?.dimensions && (
                                            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
                                                <Box sx={{ width: currentProblem.dimensions.length * 20, height: currentProblem.dimensions.width * 20, border: "2px solid #2e7d32", borderRadius: 1, bgcolor: "#e8f5e9" }} />
                                                <Typography variant="body2">{currentProblem.dimensions.length} cm × {currentProblem.dimensions.width} cm</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "cube" && currentProblem?.side !== undefined && (
                                            <Box sx={{ mb: 2 }}>
                                                <Box sx={{ width: 48, height: 48, border: "2px solid #546e7a", borderRadius: 1, bgcolor: "#eceff1" }} />
                                                <Typography variant="body2">Side: {currentProblem.side} cm</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "cuboid" && currentProblem?.dimensions && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2">{currentProblem.dimensions.l} × {currentProblem.dimensions.w} × {currentProblem.dimensions.h} cm</Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 320, mx: "auto" }}>
                                            {currentProblem?.options?.map((opt) => (
                                                <Button key={opt} variant="outlined" size="large" onClick={() => handleAnswerSubmit(opt)} disabled={!!feedback}
                                                    sx={{ py: 1.5, borderRadius: 3, fontSize: "1.25rem", fontWeight: 700, borderWidth: 2, "&:hover": { borderWidth: 2 } }}>
                                                    {opt}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : isAlgebraBasicsGame ? (
                                    <Box sx={{ py: 2 }}>
                                        <GameQuestionRich variant="h6" value={currentProblem?.question} sx={{ mb: 2, fontWeight: 700, color: "#2e7d32" }} />
                                        {currentProblem?.visualType === "notation-match" && currentProblem?.phrase && (
                                            <Box sx={{ mb: 3, p: 2, borderRadius: 3, bgcolor: "#e8f5e9", border: "2px solid #4caf50" }}>
                                                <Typography variant="body1" sx={{ fontWeight: 600, fontStyle: "italic" }}>
                                                    "{currentProblem.phrase}"
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#374151" }}>Which expression means this?</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "equation-balance" && currentProblem?.equation && (
                                            <Box sx={{
                                                mb: 3, p: 2, borderRadius: 3, bgcolor: "#e3f2fd", border: "2px solid #1976d2",
                                                display: "inline-block", mx: "auto", textAlign: "center",
                                            }}>
                                                <Typography variant="h5" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                                                    {currentProblem.equation}
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>Solve for x</Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 320, mx: "auto" }}>
                                            {currentProblem?.options?.map((opt) => (
                                                <Button
                                                    key={opt}
                                                    variant="outlined"
                                                    size="large"
                                                    onClick={() => handleAnswerSubmit(opt)}
                                                    disabled={!!feedback}
                                                    sx={{ py: 1.5, borderRadius: 3, fontSize: "1.25rem", fontWeight: 700, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
                                                >
                                                    {opt}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : isRatioProportionGame ? (
                                    <Box sx={{ py: 2 }}>
                                        <GameQuestionRich variant="h6" value={currentProblem?.question} sx={{ mb: 2, fontWeight: 700, color: "#c2185b" }} />
                                        {currentProblem?.visualType === "ratio-bar" && currentProblem?.ratio && (
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
                                                <Typography variant="h4" sx={{ fontFamily: "serif", fontWeight: 700, mb: 1 }}>
                                                    {currentProblem.ratio.a}:{currentProblem.ratio.b}
                                                </Typography>
                                                <Box sx={{ display: "flex", width: 140, height: 10, border: "2px solid #c2185b", borderRadius: 1, overflow: "hidden" }}>
                                                    <Box sx={{ width: `${100 * currentProblem.ratio.a / (currentProblem.ratio.a + currentProblem.ratio.b)}%`, bgcolor: "#e91e63", height: "100%" }} />
                                                    <Box sx={{ flex: 1, bgcolor: "#f48fb1", height: "100%" }} />
                                                </Box>
                                                <Typography variant="caption" sx={{ mt: 0.5, color: "#374151" }}>Simplify to simplest form</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "proportion-chart" && currentProblem?.proportionData && (
                                            <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: "#e8eaf6", border: "2px solid #3949ab", display: "inline-flex", gap: 2, flexWrap: "wrap" }}>
                                                <Chip label={`${currentProblem.proportionData.a} → ${currentProblem.proportionData.b}`} />
                                                <Typography sx={{ alignSelf: "center" }}>∝</Typography>
                                                <Chip label={`${currentProblem.proportionData.c} → ?`} />
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "sharing-bar" && currentProblem?.ratio && (
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
                                                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                                                    Total: {currentProblem.total} • Ratio: {currentProblem.ratio[0]}:{currentProblem.ratio[1]}
                                                </Typography>
                                                <Box sx={{ display: "flex", width: 160, height: 12, border: "2px solid #ef6c00", borderRadius: 1, overflow: "hidden" }}>
                                                    <Box sx={{ width: `${100 * currentProblem.ratio[0] / (currentProblem.ratio[0] + currentProblem.ratio[1])}%`, bgcolor: "#ff9800", height: "100%" }} />
                                                    <Box sx={{ flex: 1, bgcolor: "#ffb74d", height: "100%" }} />
                                                </Box>
                                                <Typography variant="caption" sx={{ mt: 0.5 }}>Parts: {currentProblem.ratio[0]} + {currentProblem.ratio[1]} = {currentProblem.ratio[0] + currentProblem.ratio[1]}</Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 320, mx: "auto" }}>
                                            {currentProblem?.options?.map((opt) => (
                                                <Button
                                                    key={opt}
                                                    variant="outlined"
                                                    size="large"
                                                    onClick={() => handleAnswerSubmit(opt)}
                                                    disabled={!!feedback}
                                                    sx={{ py: 1.5, borderRadius: 3, fontSize: "1.25rem", fontWeight: 700, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
                                                >
                                                    {opt}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : isFractionsDecimalsPercentagesGame ? (
                                    <Box sx={{ py: 2 }}>
                                        <GameQuestionRich variant="h6" value={currentProblem?.question} sx={{ mb: 2, fontWeight: 700, color: "#e65100" }} />
                                        {currentProblem?.visualType === "fraction-bar" && currentProblem?.fraction && (
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
                                                <Typography variant="h4" sx={{ fontFamily: "serif", fontWeight: 700, mb: 1 }}>
                                                    {currentProblem.fraction.num}/{currentProblem.fraction.den}
                                                </Typography>
                                                <Box sx={{ display: "flex", width: 120, height: 8, border: "2px solid #e65100", borderRadius: 1, overflow: "hidden" }}>
                                                    <Box sx={{ width: `${100 * currentProblem.fraction.num / currentProblem.fraction.den}%`, bgcolor: "#ff9800", height: "100%" }} />
                                                </Box>
                                                <Typography variant="caption" sx={{ mt: 0.5, color: "#374151" }}>Simplify to lowest terms</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "fdp-triple" && currentProblem?.fraction && (
                                            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center", mb: 2 }}>
                                                <Chip label={`${currentProblem.fraction.num}/${currentProblem.fraction.den}`} sx={{ fontSize: "1rem", fontWeight: 700 }} />
                                                <Typography sx={{ alignSelf: "center" }}>↔</Typography>
                                                <Chip label={currentProblem.decimal} sx={{ fontSize: "1rem", fontWeight: 700 }} />
                                                <Typography sx={{ alignSelf: "center" }}>↔</Typography>
                                                <Chip label={`${currentProblem.percent}%`} sx={{ fontSize: "1rem", fontWeight: 700 }} />
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "fraction-ops" && currentProblem?.expression && (
                                            <Box sx={{
                                                mb: 3, p: 2, borderRadius: 3, bgcolor: "#e3f2fd", border: "2px solid #1976d2",
                                                display: "inline-block", mx: "auto", textAlign: "center",
                                            }}>
                                                <Typography variant="h5" sx={{ fontFamily: "serif", fontWeight: 700 }}>
                                                    {currentProblem.expression}
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 320, mx: "auto" }}>
                                            {currentProblem?.options?.map((opt) => (
                                                <Button
                                                    key={opt}
                                                    variant="outlined"
                                                    size="large"
                                                    onClick={() => handleAnswerSubmit(opt)}
                                                    disabled={!!feedback}
                                                    sx={{ py: 1.5, borderRadius: 3, fontSize: "1.25rem", fontWeight: 700, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
                                                >
                                                    {opt}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : isArithmeticOperationsGame ? (
                                    <Box sx={{ py: 2 }}>
                                        <GameQuestionRich variant="h6" value={currentProblem?.question} sx={{ mb: 2, fontWeight: 700, color: "#5e35b1" }} />
                                        {currentProblem?.expression && (
                                            <Box sx={{
                                                mb: 3,
                                                p: 2,
                                                borderRadius: 3,
                                                bgcolor: "#f3e5f5",
                                                border: "2px solid #9c27b0",
                                                display: "inline-block",
                                                mx: "auto",
                                                textAlign: "center",
                                            }}>
                                                <Typography variant="h4" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                                                    {currentProblem.expression}
                                                </Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "bodmas-chart" && currentProblem?.bodmasOrder && (
                                            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center", mb: 2 }}>
                                                {currentProblem.bodmasOrder.map((step, i) => (
                                                    <Chip key={i} label={step} sx={{ fontWeight: 600, fontSize: "0.9rem" }} color="secondary" />
                                                ))}
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "place-shift" && currentProblem?.power !== undefined && (
                                            <Typography variant="body2" sx={{ mb: 2, color: "#374151" }}>
                                                {currentProblem.operation === "multiply" ? "→" : "←"} Shift digits by place value
                                            </Typography>
                                        )}
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 320, mx: "auto" }}>
                                            {currentProblem?.options?.map((opt) => (
                                                <Button
                                                    key={opt}
                                                    variant="outlined"
                                                    size="large"
                                                    onClick={() => handleAnswerSubmit(opt)}
                                                    disabled={!!feedback}
                                                    sx={{ py: 1.5, borderRadius: 3, fontSize: "1.25rem", fontWeight: 700, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
                                                >
                                                    {opt}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : isPieGame ? (
                                    <Box sx={{ py: 2 }}>
                                        {currentProblem?.mode === "table-to-pie" ? (
                                            <>
                                                <GameQuestionRich variant="h6" value={currentProblem?.question} sx={{ mb: 2, fontWeight: 700, color: "#2e7d32" }} />
                                                <Box
                                                    sx={{
                                                        maxWidth: 420,
                                                        mx: "auto",
                                                        mb: 3,
                                                        borderRadius: 3,
                                                        border: "2px solid #a5d6a7",
                                                        overflow: "hidden",
                                                        bgcolor: "#f1f8e9",
                                                    }}
                                                >
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                                                                <TableCell sx={{ fontWeight: 700 }} align="right">
                                                                    Percentage
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {currentProblem?.tableData?.map((row, idx) => (
                                                                <TableRow key={idx}>
                                                                    <TableCell>
                                                                        <Chip
                                                                            label={row.name}
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: row.color,
                                                                                color: "#fff",
                                                                                fontWeight: 600,
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                                        {row.value}%
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </Box>
                                                <Grid container spacing={2} sx={{ maxWidth: 480, mx: "auto", justifyContent: "center" }}>
                                                    {currentProblem?.pieOptions?.map((opt, idx) => (
                                                        <Grid item xs={12} sm={6} key={idx}>
                                                            <Box
                                                                onClick={() => !feedback && handleAnswerSubmit(String(idx))}
                                                                sx={{
                                                                    cursor: feedback ? "default" : "pointer",
                                                                    border: "3px solid",
                                                                    borderColor:
                                                                        feedback && currentProblem?.answer === String(idx)
                                                                            ? "#4caf50"
                                                                            : "#e0e0e0",
                                                                    borderRadius: 3,
                                                                    p: 1,
                                                                    "&:hover": feedback
                                                                        ? {}
                                                                        : { borderColor: "#2e7d32", bgcolor: "#e8f5e9" },
                                                                    bgcolor: "#ffffff",
                                                                }}
                                                            >
                                                                <PieChart data={opt} size={180} />
                                                            </Box>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </>
                                        ) : (
                                            <>
                                                <GameQuestionRich variant="h6" value={currentProblem?.question} sx={{ mb: 2, fontWeight: 700, color: "#2e7d32" }} />
                                                <Box sx={{ mb: 3 }}>
                                                    <PieChart data={currentProblem?.data} size={200} />
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: 1,
                                                            justifyContent: "center",
                                                            mt: 1,
                                                        }}
                                                    >
                                                        {currentProblem?.data?.map((seg, i) => (
                                                            <Chip
                                                                key={i}
                                                                size="small"
                                                                label={seg.name}
                                                                sx={{ bgcolor: seg.color, color: "#fff", fontWeight: 600 }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 2,
                                                        maxWidth: 320,
                                                        mx: "auto",
                                                    }}
                                                >
                                                    {currentProblem?.options?.map((opt) => (
                                                        <Button
                                                            key={opt}
                                                            variant="outlined"
                                                            size="large"
                                                            onClick={() => handleAnswerSubmit(opt)}
                                                            disabled={!!feedback}
                                                            sx={{
                                                                py: 1.5,
                                                                borderRadius: 3,
                                                                fontSize: "1.25rem",
                                                                fontWeight: 700,
                                                                borderWidth: 2,
                                                                "&:hover": { borderWidth: 2 },
                                                            }}
                                                        >
                                                            {opt}
                                                        </Button>
                                                    ))}
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                ) : isEnglishMcqGame ? (
                                    <Box sx={{ py: 2 }}>
                                        {currentProblem?.visualType === "passage" && currentProblem?.passage && (
                                            <Box sx={{ mb: 3, p: 2, borderRadius: 3, bgcolor: "#e3f2fd", border: "2px solid #1976d2" }}>
                                                <Typography variant="body2" sx={{ color: "#374151", mb: 0.5 }}>📖 Passage</Typography>
                                                <Typography variant="body1" sx={{ fontStyle: "italic", whiteSpace: "pre-line" }}>{currentProblem.passage}</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "word-match" && currentProblem?.word && (
                                            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
                                                <Chip label={currentProblem.word} sx={{ fontSize: "1.1rem", fontWeight: 700 }} color="secondary" />
                                                <Typography sx={{ fontSize: "1.5rem", fontWeight: 700 }}>{currentProblem.symbol}</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "sentence" && currentProblem?.sentence && (
                                            <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: "#e8f5e9", border: "2px solid #4caf50" }}>
                                                <Typography variant="body1" sx={{ fontFamily: "serif", fontSize: "1.1rem" }}>"{currentProblem.sentence}"</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "cloze-blank" && currentProblem?.sentence && (
                                            <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: "#fffde7", border: "2px solid #f9a825" }}>
                                                <Typography variant="body1" sx={{ fontFamily: "serif" }}>{currentProblem.sentence}</Typography>
                                                <Typography variant="caption" sx={{ color: "#374151" }}>Fill the blank</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "technique" && currentProblem?.example && (
                                            <Chip label={currentProblem.example} sx={{ mb: 2 }} color="secondary" />
                                        )}
                                        <GameQuestionRich variant="body1" value={currentProblem?.question} sx={{ mb: 3, fontWeight: 500, color: "#374151" }} />
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 400, mx: "auto" }}>
                                            {currentProblem?.options?.map((opt) => (
                                                <Button
                                                    key={opt}
                                                    variant="outlined"
                                                    size="large"
                                                    onClick={() => handleAnswerSubmit(opt)}
                                                    disabled={!!feedback}
                                                    sx={{ py: 1.5, borderRadius: 3, fontSize: "1.1rem", fontWeight: 600, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
                                                >
                                                    {opt}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : isVerbalReasoningGame ? (
                                    <Box sx={{ py: 2 }}>
                                        {/* Topic header with icon */}
                                        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                                            {selectedGame && (() => {
                                                const VRIcon = VERBAL_REASONING_ICON_MAP[selectedGame];
                                                return VRIcon ? <VRIcon sx={{ color: "#8e24aa", fontSize: 28 }} /> : null;
                                            })()}
                                            {currentProblem?.mode === "word-relationships" && (
                                                <><Chip label="Synonyms / Antonyms / Odd one out" size="small" sx={{ bgcolor: "#fce4ec" }} /></>
                                            )}
                                            {currentProblem?.mode === "alphabet-logic" && (
                                                <><Chip label="Letters & sequences" size="small" sx={{ bgcolor: "#e8eaf6" }} /></>
                                            )}
                                            {currentProblem?.mode === "word-puzzles" && (
                                                <><Chip label="Hidden & compound words" size="small" sx={{ bgcolor: "#e1f5fe" }} /></>
                                            )}
                                            {currentProblem?.mode === "codes-logic" && (
                                                <><Chip label="Letter-number codes" size="small" sx={{ bgcolor: "#fff8e1" }} /></>
                                            )}
                                            {currentProblem?.mode === "sequence-pattern" && (
                                                <><Chip label="Pattern & series" size="small" sx={{ bgcolor: "#e8f5e9" }} /></>
                                            )}
                                            {currentProblem?.mode === "comprehension" && (
                                                <><Chip label="Read & infer" size="small" sx={{ bgcolor: "#f3e5f5" }} /></>
                                            )}
                                        </Box>
                                        {/* Comprehension: passage box */}
                                        {currentProblem?.visualType === "passage-inference" && currentProblem?.passage && (
                                            <Box sx={{ mb: 3, p: 2.5, borderRadius: 3, bgcolor: "#f3e5f5", border: "2px solid #ce93d8" }}>
                                                <Typography variant="overline" sx={{ fontWeight: 700, color: "#7b1fa2" }}>Passage</Typography>
                                                <Typography variant="body1" sx={{ mt: 0.5, fontStyle: "italic", color: "#4a148c" }}>
                                                    "{currentProblem.passage}"
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600, color: "#6a1b9a" }}>
                                                    {currentProblem.inferenceQuestion}
                                                </Typography>
                                            </Box>
                                        )}
                                        {/* Word relationships: focus word / word group */}
                                        {currentProblem?.visualType === "synonym-pair" && currentProblem?.focusWord && (
                                            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                                <CompareArrowsIcon sx={{ color: "#c2185b", fontSize: 24 }} />
                                                <Chip label={currentProblem.focusWord} sx={{ bgcolor: "#fce4ec", fontWeight: 700 }} />
                                                <Typography variant="body2" color="text.secondary">→ synonym?</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "antonym-pair" && currentProblem?.focusWord && (
                                            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                                <CompareArrowsIcon sx={{ color: "#c2185b", fontSize: 24 }} />
                                                <Chip label={currentProblem.focusWord} sx={{ bgcolor: "#fce4ec", fontWeight: 700 }} />
                                                <Typography variant="body2" color="text.secondary">→ opposite?</Typography>
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "odd-one-out" && currentProblem?.wordGroup?.length > 0 && (
                                            <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
                                                <Typography variant="caption" sx={{ width: "100%", textAlign: "center", fontWeight: 600 }}>Odd one out:</Typography>
                                                {currentProblem.wordGroup.map((w, i) => (
                                                    <Chip key={i} label={w} sx={{ bgcolor: "#fff3e0", fontWeight: 600 }} />
                                                ))}
                                            </Box>
                                        )}
                                        {/* Alphabet: letter sequence / code chart */}
                                        {currentProblem?.visualType === "letter-sequence" && currentProblem?.sequence?.length > 0 && (
                                            <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", alignItems: "center" }}>
                                                <SortByAlphaIcon sx={{ color: "#3949ab", fontSize: 24 }} />
                                                {currentProblem.sequence.map((s, i) => (
                                                    <Chip key={i} label={s} sx={{ bgcolor: "#e8eaf6", fontWeight: 700, fontSize: "1rem" }} />
                                                ))}
                                            </Box>
                                        )}
                                        {currentProblem?.visualType === "anagram" && currentProblem?.jumbled && (
                                            <Box sx={{ mb: 2, p: 2, borderRadius: 3, bgcolor: "#e8eaf6", border: "2px solid #7986cb", display: "inline-flex", alignItems: "center", gap: 1 }}>
                                                <ExtensionIcon sx={{ color: "#3949ab" }} />
                                                <Typography variant="h6" sx={{ fontFamily: "monospace", fontWeight: 700, letterSpacing: 2 }}>{currentProblem.jumbled}</Typography>
                                                <Typography variant="caption" color="text.secondary">→ rearrange</Typography>
                                            </Box>
                                        )}
                                        {/* Codes: chart */}
                                        {(currentProblem?.visualType === "code-chart" || currentProblem?.visualType === "number-to-word") && currentProblem?.codeChart?.length > 0 && (
                                            <Box sx={{ mb: 2, p: 2, borderRadius: 3, bgcolor: "#fff8e1", border: "2px solid #ffb74d", display: "inline-flex", flexWrap: "wrap", gap: 1 }}>
                                                <CodeIcon sx={{ color: "#ff8f00", fontSize: 24 }} />
                                                {currentProblem.codeChart.map(([k, v], i) => (
                                                    <Chip key={i} label={`${k}=${v}`} size="small" sx={{ bgcolor: "#fff" }} />
                                                ))}
                                                {currentProblem.codedWord && <Typography variant="body2" sx={{ alignSelf: "center" }}>→ {currentProblem.codedWord}</Typography>}
                                                {currentProblem.code && <Typography variant="body2" sx={{ alignSelf: "center" }}>→ {currentProblem.code}</Typography>}
                                            </Box>
                                        )}
                                        {/* Compound word */}
                                        {currentProblem?.visualType === "compound-word" && currentProblem?.parts?.length >= 2 && (
                                            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                                                <Chip label={currentProblem.parts[0]} sx={{ bgcolor: "#e1f5fe", fontWeight: 700 }} />
                                                <Typography sx={{ fontWeight: 700, color: "#0277bd" }}>+</Typography>
                                                <Chip label={currentProblem.parts[1]} sx={{ bgcolor: "#e1f5fe", fontWeight: 700 }} />
                                                <Typography variant="body2" color="text.secondary">= ?</Typography>
                                            </Box>
                                        )}
                                        {/* Hidden word */}
                                        {currentProblem?.visualType === "hidden-word" && currentProblem?.hostWord && (
                                            <Box sx={{ mb: 2, p: 2, borderRadius: 3, bgcolor: "#e1f5fe", border: "2px dashed #0288d1" }}>
                                                <Typography variant="overline" sx={{ color: "#0277bd", fontWeight: 700 }}>Find word inside</Typography>
                                                <Typography variant="h5" sx={{ fontFamily: "monospace", letterSpacing: 3, fontWeight: 700, mt: 0.5 }}>{currentProblem.hostWord}</Typography>
                                            </Box>
                                        )}
                                        {/* Sequence / series */}
                                        {(currentProblem?.visualType === "word-series" || currentProblem?.visualType === "letter-series" || currentProblem?.visualType === "comparative-series") && currentProblem?.series?.length > 0 && (
                                            <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", alignItems: "center" }}>
                                                <TimelineIcon sx={{ color: "#2e7d32", fontSize: 24 }} />
                                                {currentProblem.series.map((s, i) => (
                                                    <Chip key={i} label={s} sx={{ bgcolor: "#e8f5e9", fontWeight: 600 }} />
                                                ))}
                                            </Box>
                                        )}
                                        {/* Code rule example */}
                                        {currentProblem?.visualType === "letter-shift" && currentProblem?.example && (
                                            <Box sx={{ mb: 2, p: 2, borderRadius: 3, bgcolor: "#fff8e1", border: "2px solid #ffb74d" }}>
                                                <Chip label={`Rule: ${currentProblem.rule}`} size="small" sx={{ mb: 1 }} />
                                                <Typography variant="body1">{currentProblem.example}</Typography>
                                            </Box>
                                        )}
                                        {!currentProblem?.passage && (
                                            <GameQuestionRich
                                                variant="body1"
                                                value={currentProblem?.question}
                                                sx={{ mb: 3, fontWeight: 500, color: "#374151", whiteSpace: "pre-line" }}
                                            />
                                        )}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 2,
                                                maxWidth: 400,
                                                mx: "auto",
                                            }}
                                        >
                                            {currentProblem?.options?.map((opt) => (
                                                <Button
                                                    key={opt}
                                                    variant="outlined"
                                                    size="large"
                                                    onClick={() => handleAnswerSubmit(opt)}
                                                    disabled={!!feedback}
                                                    sx={{
                                                        py: 1.5,
                                                        borderRadius: 3,
                                                        fontSize: "1.1rem",
                                                        fontWeight: 600,
                                                        borderWidth: 2,
                                                        "&:hover": { borderWidth: 2 },
                                                    }}
                                                >
                                                    {opt}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : isNonVerbalReasoningGame ? (
                                    <Box sx={{ py: 3 }}>
                                        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                                            {currentProblem?.stimulus?.type === "sequence" && (
                                                <ShapeSequence shapes={currentProblem.stimulus.shapes} />
                                            )}
                                            {currentProblem?.stimulus?.type === "matrix" && (
                                                <ShapeGrid
                                                    shapes={currentProblem.stimulus.shapes}
                                                    cols={currentProblem.stimulus.cols || 2}
                                                />
                                            )}
                                            {currentProblem?.stimulus?.type === "analogy" && (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                    <ShapeSequence shapes={currentProblem.stimulus.pair1} />
                                                    <Typography sx={{ color: "#374151", fontSize: "1.5rem" }}>→</Typography>
                                                    <ShapeSequence shapes={currentProblem.stimulus.pair2} />
                                                    <Typography sx={{ color: "#374151", fontWeight: 600 }}>?</Typography>
                                                </Box>
                                            )}
                                            {currentProblem?.stimulus?.type === "oddout" && (
                                                <ShapeSequence shapes={currentProblem.stimulus.shapes} />
                                            )}
                                            {currentProblem?.stimulus?.type === "single" && (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                    <ShapeBlock shape={currentProblem.stimulus.shape} />
                                                    <Typography sx={{ color: "#374151", fontSize: "1.2rem" }}>→</Typography>
                                                    <Typography sx={{ color: "#374151" }}>?</Typography>
                                                </Box>
                                            )}
                                            {currentProblem?.stimulus?.type === "codes" && (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                    <ShapeSequence shapes={currentProblem.stimulus.before} />
                                                    <Typography sx={{ color: "#374151" }}>→</Typography>
                                                    <Typography sx={{ color: "#374151" }}>?</Typography>
                                                </Box>
                                            )}
                                            {currentProblem?.stimulus?.type === "hidden" && (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <ShapeSequence shapes={currentProblem.stimulus.shapes} gap={0.5} />
                                                    <Typography sx={{ color: "#374151", ml: 1 }}>?</Typography>
                                                </Box>
                                            )}
                                            {currentProblem?.stimulus?.type === "text" && (
                                                <GameQuestionRich
                                                    variant="h6"
                                                    value={currentProblem.stimulus.question}
                                                    sx={{
                                                        mb: 0,
                                                        fontWeight: 700,
                                                        color: "#1565c0",
                                                        whiteSpace: "pre-line",
                                                        textAlign: "center",
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Typography
                                            sx={{
                                                textAlign: "center",
                                                color: "#374151",
                                                mb: 2,
                                                fontSize: "0.95rem",
                                            }}
                                        >
                                            {currentProblem?.mode === "odd-one-out"
                                                ? "Which does not belong?"
                                                : "Which one?"}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 2,
                                                justifyContent: "center",
                                            }}
                                        >
                                            {currentProblem?.options?.map((opt, idx) => (
                                                <Button
                                                    key={idx}
                                                    variant="outlined"
                                                    onClick={() =>
                                                        handleAnswerSubmit(
                                                            typeof opt === "object" && opt.t ? String(idx) : opt
                                                        )
                                                    }
                                                    disabled={!!feedback}
                                                    sx={{
                                                        p: 2,
                                                        minWidth: 80,
                                                        borderWidth: 2,
                                                        borderRadius: 3,
                                                        "&:hover": { borderWidth: 2 },
                                                    }}
                                                >
                                                    {typeof opt === "object" && opt.t ? (
                                                        <ShapeBlock shape={opt} size={44} />
                                                    ) : (
                                                        <Typography
                                                            sx={{ fontSize: "1.4rem", fontWeight: 700, minWidth: 24 }}
                                                        >
                                                            {opt}
                                                        </Typography>
                                                    )}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : (
                                    <EnglishGameUI />
                                )}

                                {feedback && (
                                    <Paper sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        p: { xs: 1.5, sm: 2, md: 3 },
                                        borderRadius: 4,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        color: feedback.type === 'success' ? '#065f46' : '#991b1b',
                                        bgcolor: feedback.type === 'success' ? '#f0fdf4' : '#fef2f2',
                                        border: `2px solid ${feedback.type === 'success' ? '#10b981' : '#ef4444'}`,
                                        zIndex: 10,
                                        boxShadow: '0 20px 50px rgba(0,0,0,0.1)',
                                        animation: 'popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                        '@keyframes popIn': {
                                            from: { transform: 'translate(-50%, -50%) scale(0.5)', opacity: 0 },
                                            to: { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 }
                                        }
                                    }}>
                                        {feedback.type === 'success' ? <SuccessIcon sx={{ fontSize: 60 }} /> : <ErrorIcon sx={{ fontSize: 60 }} />}
                                        <Typography variant="h4" fontWeight="bold">
                                            {feedback.message}
                                        </Typography>
                                    </Paper>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default Games;
